import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  Subject,
  Observable,
  Subscription,
  ObservableInput,
  catchError,
  defer,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  shareReplay,
  throwError,
  timer,
  switchMap,
  takeUntil,
  of,
  from
} from 'rxjs';
import { mergeMap, retry, retryWhen } from 'rxjs/operators';
import { ImageRef, Message } from '../models/chat.model';
import { environment } from '../../environments/environment';
import { ErrorLoggerService } from '../services/error-logger.service';
import { getAuth } from 'firebase/auth';
import { ReasoningService, ReasoningLevel } from '../services/reasoning.service';
import { VideoFireService, VideoCreateRequest, VideoJob } from './video-fire.service';
import { UiService, UiVideoJobUpdate, OpenAiRtcSession } from './ui.service';
import { LiveTranscriptChunk } from '../services/googleaudio.service';

export type VideoJobStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface VeoVideoGenOptions {
  prompt: string;
  uid: string;
  sessionId: string;
  modelId?: string;
  seconds?: 4 | 6 | 8;
  aspectRatio?: '16:9' | '9:16';
  resolution?: '720p' | '1080p';
  imageBase64?: string;
  lastFrameBase64?: string;
}

export interface VideoJobSession {
  uid?: string;
  sessionId: string;
}

export interface VideoStartRequest {
  action: 'start';
  prompt: string;
  uid: string;
  sessionId: string;
  session: VideoJobSession;
  modelId?: string;
  duration_seconds: 4 | 6 | 8;
  aspect_ratio: '16:9' | '9:16';
  resolution: '720p' | '1080p';
  image?: string;
  lastFrameBase64?: string;
}

export interface QwenCoderChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface QwenCoderChatResponse {
  reply: string;
  text?: string;
  model_output?: string;
  history: QwenCoderChatMessage[];
  modelId?: string;
  requestId?: string;
  stopReason?: string;
  usage?: Record<string, unknown>;
}

export interface VideoPollRequest {
  action: 'poll';
  id: string;
  session: VideoJobSession;
  max_wait_seconds: number;
  poll_interval_seconds: number;
}

export interface VideoStartResponse {
  id?: string | null;
  operation_name?: string | null;
  status?: string | null;
  url?: string | null;
  video_uri?: string | null;
  error?: string | null;
  session?: VideoJobSession;
  modelId?: string;
  imageModel?: any;
}

export interface VideoPollResponse extends VideoStartResponse {}

export interface CombinedSummaryResponse {
  summary: string;
  analysis?: string;
  relevance?: string;
  furtherAnalysis?: string;
  preferredModel?: string;
  [key: string]: any;
}

const DEFAULT_GEMINI_DOCS_LAMBDA_URL =
  'https://v2quhhx6duwsv3i4m7gcbrlafm0paopu.lambda-url.us-east-1.on.aws/';
const DEFAULT_GEMINI_CHAT_LAMBDA_URL =
  'https://k2orvt5ph5hljzmaroll5li4rq0egxpx.lambda-url.us-east-1.on.aws/';
const DEFAULT_STABILITY_LAMBDA_URL =
  'https://pn3pvls4q4vhgx46r6rxfnmrry0bhunf.lambda-url.us-west-2.on.aws/';
const DEFAULT_COGX_LAMBDA_URL =
  'https://ls55ggjcjir7oaojdbmcvfwtju0qqvqe.lambda-url.us-west-2.on.aws/';
const DEFAULT_VEO31_CLIENT_LAMBDA_URL =
  'https://7rysumw5zim6l5pq46caij5oru0tsytd.lambda-url.us-east-1.on.aws/';
const PEGASUS_LAMBDA_URL =
  'https://53b4fxgg4elkhagkvlpnpqqeo40lqogd.lambda-url.us-east-1.on.aws/';
const PEGASUS_MODEL_ID =
  'arn:aws:bedrock:us-east-1:203918849532:inference-profile/global.twelvelabs.pegasus-1-2-v1:0';

export interface VideoJobUpdate {
  id: string;
  operation_name?: string | null;
  status: VideoJobStatus;
  progress?: number;
  url: string | null;
  videoUrl: string | null;
  thumbnailUrl?: string | null;
  error: string | null;
  session?: VideoJobSession;
  modelId?: string;
  imageModel?: any;
}

export interface PegasusHistoryItem {
  role: 'user' | 'assistant';
  content: string;
}

export interface PegasusMediaSource {
  base64String?: string;
  s3Location?: {
    uri: string;
    bucketOwner?: string;
  };
}

export interface PegasusResponseFormat {
  type: 'json_schema';
  json_schema: {
    name: string;
    schema: Record<string, unknown>;
  };
}

export interface PegasusLambdaRequest {
  modelId?: string;
  inputPrompt: string;
  mediaSource: PegasusMediaSource;
  temperature?: number;
  maxOutputTokens?: number;
  responseFormat?: PegasusResponseFormat;
  history?: PegasusHistoryItem[];
}

export interface PegasusLambdaResponse {
  text: string;
  model_output: string;
  model_id: string;
  history: PegasusHistoryItem[];
  finishReason?: 'stop' | 'length' | string;
  structured_output?: unknown;
}

export interface StabilityLambdaResponse {
  model_id?: string;
  output_format?: string;
  images?: string[];
  seeds?: Array<number | null>;
  image?: string;
  seed?: number | null;
  error?: string;
  finish_reasons?: any;
}

type RealtimeTransportMessage =
  | { type: 'rtc.offer'; sdp: string; sessionId: string }
  | { type: 'rtc.answer'; sdp: string }
  | { type: 'rtc.ice'; candidate: RTCIceCandidateInit; sessionId?: string }
  | { type: 'oai.client.event'; event: any; sessionId?: string }
  | { type: 'oai.server.event'; event: any; sessionId?: string }
  | { type: 'session.resume'; sessionId: string }
  | { type: 'ping'; ts: number; sessionId?: string }
  | { type: 'pong'; ts?: number; sessionId?: string }
  | { type: 'error'; error: any; sessionId?: string }
  | { type: string; [key: string]: any };

interface ServerRtcSessionBootstrap {
  sessionId: string;
  wsUrl: string;
}

interface InternalOpenAiRtcSession {
  sessionId: string;
  events$: Observable<any>;
  pc: RTCPeerConnection;
  dc: RTCDataChannel;
  localStream: MediaStream | null;
  remoteAudioEl: HTMLAudioElement;
  sendText: (text: string) => void;
  muteMic: (muted: boolean) => void;
  close: () => void;
}

export type AnswerLength = 'short' | 'normal' | 'long' | 'detailed';

@Injectable({ providedIn: 'root' })
export class AIFireService implements OnDestroy {
  private static readonly retryModelOrder = [
    'palmx',
    'haiku45',
    'deepv3',
    'commandLight',
    'scout'
  ];

  /** Older same-vendor / same-family endpoints to try before the global retry pool. */
  private static readonly modelVersionFallbackChain: Readonly<Record<string, readonly string[]>> = {
    sonnet45: ['claude4'],
    opus47: ['opus46'],
    opus46: ['opuslegacy'],
    maverick: ['llama'],
    scout: ['maverick'],
    deepv3: ['deepseek'],
    gemini3flash: ['gemini']
  };

  private readonly realtimeHeartbeatMs = 15000;
  private readonly realtimeMaxReconnectAttempts = 3;
  private readonly realtimeWsOpenTimeoutMs = 10000;
  private readonly realtimeReconnectBaseDelayMs = 1000;
  private readonly beforeUnloadHandler = () => {
    this.cleanupRealtimeSession();
  };

  // ---------- Lambda / vendor endpoints ----------

  private chatgpt54 = 'https://zmqf3ltoirqgqhscnjshvn3tym0lxegx.lambda-url.us-east-1.on.aws/';
  private miniChatGPT54 = 'https://lnukhx6apmcota3kizpzilm3wq0eebip.lambda-url.us-east-1.on.aws/';
  /** IQ Eye combined summary — Claude Sonnet 4.6 Lambda (us-east-1). */
  private readonly iqSummarySonnet46LambdaUrl =
    'https://dnpdxprqnoaopgmlozm7gx4lzu0yjwgb.lambda-url.us-east-1.on.aws/';
  /** Multichat / other callers that pass an explicit Bedrock `modelId`. */
  private summaryLambdaUrl = 'https://2b54o4cfwwbsh6fhuk7tkbmieu0epltl.lambda-url.us-east-1.on.aws/';
  private static readonly iqCombinedSummaryModelId = 'sonnet4.6';
  private sonnet45Chat = 'https://fxs73g34hx7fcklasmdugbecfy0uzftt.lambda-url.us-east-1.on.aws/';
  private claude4ChatImage = 'https://7yh7ocg3fd5afpakrqx5krxirq0rsiuf.lambda-url.us-east-1.on.aws/';
  private pistralImageChat = 'https://qcqujid7p3ndr2lerm7yfxnbmu0trvnk.lambda-url.us-east-1.on.aws/';
  private nova2Chat = 'https://6ihvrh53rxtyd6zmrmsjjtfzyi0jzbni.lambda-url.us-east-1.on.aws/';
  private stabilityAPI = DEFAULT_STABILITY_LAMBDA_URL;
  /** Stability SD 3.5 Large (Bedrock) — JSON body per Lambda handler. */
  private stabilitySd35LargeUrl =
    'https://mj3wu75smqrnzpnzxzshwjjnpy0tvqkk.lambda-url.us-east-1.on.aws/';
  private scoutLambda1 = 'https://z6n6pwj3gx2kgq7vcge7hmtdi40pfyqd.lambda-url.us-east-1.on.aws/';
  private ClaudeChat = 'https://zl3itj6lpuzspqp6trle3s5psa0ggsbf.lambda-url.us-east-1.on.aws/';
  private LlamaChat = 'https://b4l67eprbwjr2bdmhsnww7dfuu0tfcom.lambda-url.us-east-1.on.aws/';
  private CohereChat = 'https://wmqhliujfxwwmwv2bn7ec223ue0bbjbg.lambda-url.us-west-2.on.aws/';
  private deepseekChat = 'https://z6obt2xs6pxjxxjuhij5nnso2y0sgmqm.lambda-url.us-east-1.on.aws/';
  private commandLight = 'https://p7uhjowh55txspa74ufko4adom0ljhrt.lambda-url.us-east-1.on.aws/';
  private opusChat = 'https://juiiirtlxuo22a3zzrmyct3rse0xbopx.lambda-url.us-east-1.on.aws/';
  private opus37Chat = 'https://t4fxzkikt3fiqyxhpyobc2ly5q0kngnh.lambda-url.us-west-2.on.aws/';
  private opus6StreamChat = 'https://bps4bie4zqdgqhawcij3y2zuay0tyxrs.lambda-url.us-east-1.on.aws/';
  /** Claude Opus 4.6 — Bedrock Converse Lambda (prompt, history, optional systemPrompt, inferenceConfig). */
  private readonly opus46BedrockConverseLambdaUrl =
    'https://o7nvj4v6rnouysrhhnt2uk3cqa0tzlza.lambda-url.us-east-1.on.aws/';
  /** Claude Opus 4.7 — same Bedrock Converse Lambda; server may branch on `catalogModelId`. */
  private readonly opus47BedrockConverseLambdaUrl =
    'https://o7nvj4v6rnouysrhhnt2uk3cqa0tzlza.lambda-url.us-east-1.on.aws/';
  /** Kimi K2.5 (Moonshot AI) — Function URL (us-east-1). */
  private readonly kimiK25ChatUrl =
    'https://fdcyl7u3ubgouxa5cjftie474q0zpfwu.lambda-url.us-east-1.on.aws/';
  private mistralLargeChat = 'https://l4crtb6oayhhbjdva2pg52kfim0bfond.lambda-url.us-west-2.on.aws/';
  private codestraltoChat = 'https://seldkvt7rvx5zqutyryvbmzeji0japcy.lambda-url.us-west-2.on.aws/';
  private maverickChat = 'https://b4l67eprbwjr2bdmhsnww7dfuu0tfcom.lambda-url.us-east-1.on.aws/';
  private novaProChat = 'https://6yxs6n2wu6n5s6g4wadprvt6b40dveka.lambda-url.us-east-1.on.aws/';
  private jambaChat = 'https://jpconpv7urus4rypuq7qxg6cqy0iqydo.lambda-url.us-east-1.on.aws/';
  /** Claude Haiku 4.5 chat Lambda (us-east-1). */
  private haiku45Chat = 'https://z4lrwzsyhh7cogjmxtdhuah3fe0fkiql.lambda-url.us-east-1.on.aws/';
  private palmxChat = 'https://lqnybor2lmp532xj6wf6etzwfm0hgegn.lambda-url.us-east-1.on.aws/';
  private readonly pegasusLambdaUrl = PEGASUS_LAMBDA_URL;
  private openAIKey = String((environment as any).OPENAI_API_KEY || '').trim();
  private readonly openAIResponsesUrl = 'https://api.openai.com/v1/responses';
  
  private readonly geminiModel = 'gemini-3-flash-preview';
  private readonly geminiUrl = this.preferLambdaUrl((environment as any).geminiEndpoint, DEFAULT_GEMINI_CHAT_LAMBDA_URL);
  private readonly geminiProImageUrl = 'https://i7sesxfymjd7zx2nw3vcjhyesi0ixpfp.lambda-url.us-east-1.on.aws/';
  private readonly openAIEndpoint = (environment as any).openAIEndpoint;
  openAliEndpoint = 'https://zmqf3ltoirqgqhscnjshvn3tym0lxegx.lambda-url.us-east-1.on.aws/';
  /** DeepSeek V3.2 chat Lambda (us-west-2). */
  private deepSeekV32Chat = 'https://g5vcwe75rh7qelnj4ezfx7znoe0iudsm.lambda-url.us-west-2.on.aws/';
  
  private readonly cogxLambdaUrl = this.preferLambdaUrl((environment as any).cogxLambdaUrl, DEFAULT_COGX_LAMBDA_URL)
    .replace(/\/+$/, '');
  private readonly novaReel11LambdaUrl = String((environment as any).novaReel11LambdaUrl || '').trim();
  
  private readonly zaiApikey =
    ((environment as any).GLM_API_KEY || environment.zaiApikey || '').trim();
  private readonly zaiBaseUrl =
    ((environment as any).zaiBaseUrl?.trim?.() as string) ||
    'https://api.z.ai/api/paas/v4';
  private readonly zaiChatCompletionsUrl = `${this.zaiBaseUrl.replace(/\/+$/, '')}/chat/completions`;
  private readonly zaiVideosGenerationsUrl = 'https://jaje2d7vo2ybgj4nzjzkua6kyi0wpddy.lambda-url.us-east-1.on.aws/ ';
  private readonly zaiAsyncResultUrl = `${this.zaiBaseUrl.replace(/\/+$/, '')}/async-result`;
  
  private vertexClaudeChatUrl = (environment.vertexClaudeChatUrl || '').trim();
  private awsOpus45 = 'https://juiiirtlxuo22a3zzrmyct3rse0xbopx.lambda-url.us-east-1.on.aws/';
  private awsSonnet45 = 'https://fxs73g34hx7fcklasmdugbecfy0uzftt.lambda-url.us-east-1.on.aws/';
  private vertexClaudeApiKey = (environment.vertexClaudeApiKey || '').trim();

  private readonly glm5Chat =
    'https://lc45qm5jfokkrpklokq3aeicze0tjupo.lambda-url.us-east-1.on.aws/';
  private readonly qwenChat =
    'https://2xow4z6aukuzk3erdztabiuoyy0clxls.lambda-url.us-east-1.on.aws/';

  
  // ---------- OpenAI / X.ai / Google ----------
  private readonly geminiDocsLambdaUrl =
    String((environment as any).geminiDocsLambdaUrl || DEFAULT_GEMINI_DOCS_LAMBDA_URL).trim();
  
  // ---------- Realtime model ----------
  private readonly REALTIME_MODEL =
    String((environment as any).openAIRealtimeModel || 'gpt-4o-realtime-preview-2025-02-15').trim();
  private readonly REALTIME_TRANSCRIBE_MODEL =
    String((environment as any).openAIRealtimeTranscribeModel || 'gpt-4o-mini-transcribe').trim();
  private readonly openAIRealtimeSessionsUrl =
    String((environment as any).openAIRealtimeSessionsUrl || 'https://api.openai.com/v1/realtime/sessions').trim();
  private readonly openAIRealtimeProxyUrl =
    String(
      (environment as any).openAIRealtimeProxyUrl ||
      'https://ir2gskmzuokcdwb7cprvamifwu0pxzbe.lambda-url.us-east-1.on.aws/rtc/openai'
    ).trim();
  private readonly openAIRealtimeSessionUrl =
    String((environment as any).openAIRealtimeSessionUrl || '').trim();
  
  // ---------- Conversational audio defaults ----------
  private readonly DEFAULT_VOICE = 'verse';
  private readonly DEFAULT_LOCALE = 'en-US';
  private readonly DEFAULT_TIMEZONE = 'America/New_York';
  
  private activeRealtime:
    | {
        pc: RTCPeerConnection;
        dc: RTCDataChannel;
        localStream: MediaStream | null;
        remoteAudioEl: HTMLAudioElement;
        sendText: (text: string) => void;
        muteMic: (muted: boolean) => void;
        close: () => void;
      }
    | null = null;
  
  // ---------- Cross-component reactive streams ----------
  readonly sessionActive$ = new BehaviorSubject<boolean>(false);
  readonly micActive$ = this.sessionActive$;
  readonly textDelta$ = new Subject<string>();
  readonly textDone$ = new Subject<void>();
  readonly error$ = new Subject<any>();
  readonly partialUserTranscript$ = new BehaviorSubject<string>('');
  readonly userTranscriptDelta$ = new Subject<string>();
  readonly userTranscriptDone$ = new Subject<string>();
  readonly remoteAudioEl$ = new BehaviorSubject<HTMLAudioElement | null>(null);
  
  private readonly videoApiBaseUrl =
    (environment as any).videoApiBaseUrl ||
    'https://clarichat-video-api-630346558823.us-central1.run.app';
  
  private readonly videoJobsBaseUrl =
    (environment as any).videoJobsBaseUrl ||
    'https://us-central1-clarichatai.cloudfunctions.net/mediaList';
  
  private readonly veo31ClientLambdaUrl =
    (environment as any).veo31ClientLambdaUrl || DEFAULT_VEO31_CLIENT_LAMBDA_URL;
  private readonly defaultPollMaxWaitSeconds = 300;
  private readonly defaultPollIntervalSeconds = 3;
  
  // ---------- Web Audio analysers for visualizers ----------
  private audioCtx: AudioContext | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private remoteAnalyser: AnalyserNode | null = null;
  
  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private errorLogger: ErrorLoggerService,
    private reasoningService: ReasoningService,
    private videoFire: VideoFireService,
    private uiService: UiService
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }
  
  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    this.cleanupRealtimeSession();
  }

  private preferLambdaUrl(candidate: unknown, fallback: string): string {
    const value = String(candidate || '').trim();
    return value.includes('.lambda-url.') ? value : fallback;
  }
  
  private normalizeGpt5ReasoningLevel(level?: string | null): 'low' | 'medium' | 'high' | undefined {
    if (level === 'low' || level === 'medium' || level === 'high') {
      return level;
    }
    return undefined;
  }
  
  getRetryModelOrder(): string[] {
    return [...AIFireService.retryModelOrder];
  }
  
  getAvailableRetryModels(exclude: string[] = []): string[] {
    const excluded = new Set(exclude);
    return AIFireService.retryModelOrder.filter((model) => !excluded.has(model));
  }
  
  markRetryModelFailed(modelId: string): void {
    void modelId;
  }

  getVersionFallbackModels(modelId: string): string[] {
    const out: string[] = [];
    const visited = new Set<string>([modelId]);
    const walk = (id: string) => {
      const chain = AIFireService.modelVersionFallbackChain[id];
      if (!chain) return;
      for (const next of chain) {
        if (visited.has(next)) continue;
        visited.add(next);
        out.push(next);
        walk(next);
      }
    };
    walk(modelId);
    return out;
  }

  /**
   * Primary model, then in-family older endpoints, then global retry pool (deduped).
   */
  buildChatModelsToTry(requestedModel: string, maxGlobalRetries: number): string[] {
    const fallbacks = this.getVersionFallbackModels(requestedModel);
    const seen = new Set<string>();
    const ordered: string[] = [];
    const push = (id: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      ordered.push(id);
    };
    push(requestedModel);
    for (const id of fallbacks) push(id);
    const exclude = [...ordered];
    const randomizedRetryPool = this.shuffleModels(this.getAvailableRetryModels(exclude));
    for (const id of randomizedRetryPool.slice(0, maxGlobalRetries)) push(id);
    return ordered;
  }

  private shuffleModels(models: string[]): string[] {
    const out = [...models];
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }
  
  getAnalyzers() {
    return { audioCtx: this.audioCtx, mic: this.micAnalyser, remote: this.remoteAnalyser };
  }
  
  private ensureAudioContext(): AudioContext {
    if (!this.audioCtx) {
      this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioCtx;
  }
  
  private bindRealtimeAnalyzers(localStream: MediaStream | null, remoteAudioEl: HTMLAudioElement | null): void {
    try {
      const ctx = this.ensureAudioContext();
      
      if (this.micAnalyser) {
        try { this.micAnalyser.disconnect(); } catch {}
      }
      if (this.remoteAnalyser) {
        try { this.remoteAnalyser.disconnect(); } catch {}
      }
      
      this.micAnalyser = null;
      this.remoteAnalyser = null;
      
      if (localStream) {
        const micSource = ctx.createMediaStreamSource(localStream);
        this.micAnalyser = ctx.createAnalyser();
        this.micAnalyser.fftSize = 2048;
        micSource.connect(this.micAnalyser);
      }
      
      if (remoteAudioEl && (remoteAudioEl as any).captureStream) {
        const remoteStream = (remoteAudioEl as any).captureStream();
        if (remoteStream) {
          const remoteSource = ctx.createMediaStreamSource(remoteStream);
          this.remoteAnalyser = ctx.createAnalyser();
          this.remoteAnalyser.fftSize = 2048;
          remoteSource.connect(this.remoteAnalyser);
        }
      }
    } catch {
      // visualizers are best-effort only
    }
  }
  
  private resetRealtimeAnalyzers(): void {
    try { this.micAnalyser?.disconnect(); } catch {}
    try { this.remoteAnalyser?.disconnect(); } catch {}
    this.micAnalyser = null;
    this.remoteAnalyser = null;
  }
  
  private getCurrentUserId(): string {
    const auth = getAuth();
    return auth.currentUser?.uid || 'anonymous';
  }
  
  private sanitizeHistoryForApi(history: Message[]): Array<{ role: string; content: string }> {
    return (history || [])
      .map((msg) => ({
        role: msg.role || 'user',
        content: String(msg.content || '').trim()
      }))
      .filter((msg) => msg.content);
  }
  
  private extractZaiText(resp: any): string {
    const payload =
      resp?.data && !resp?.choices && typeof resp?.data === 'object'
        ? resp.data
        : resp;
    
    const choices = payload?.choices;
    if (Array.isArray(choices) && choices.length) {
      const first = choices[0] || {};
      const message = first.message || {};
      const content = message.content;
      if (typeof content === 'string' && content.trim()) {
        return content.trim();
      }
      if (Array.isArray(content)) {
        const joined = content
          .map((part) => (typeof part === 'string' ? part : part?.text || part?.content || ''))
          .join('')
          .trim();
        if (joined) return joined;
      }
      const text = first.text;
      if (typeof text === 'string' && text.trim()) {
        return text.trim();
      }
    }
    
    for (const key of ['outputText', 'generated_text', 'text', 'completion']) {
      const value = payload?.[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    
    return '';
  }
  
  private getJsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }
  
  private payloadKey(obj: any): string {
    return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
  }
  
  private get headers(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this.openAIKey}`,
      'Content-Type': 'application/json'
    });
  }
  
  private fileToDataUrl(file: File): Observable<string> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = () => observer.error(new Error('Failed to read file'));
      reader.readAsDataURL(file);
      return () => reader.abort();
    });
  }
  
  async createGoogleDocFromText(
    title: string,
    bodyText: string,
    accessToken: string,
    refreshToken?: string
  ): Promise<{ id: string; name: string; url?: string }> {
    const payload: any = {
      prompt: this.buildGoogleDocPrompt(title, bodyText),
      length: 'normal',
      enable_google: true,
      google_access_token: accessToken
    };
    
    if (refreshToken) {
      payload.google_refresh_token = refreshToken;
    }
    
    const response = await firstValueFrom(
      this.http.post<any>(this.geminiDocsLambdaUrl, payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      })
    );
    
    const toolCalls = (response?.tool_calls || response?.toolCalls || []) as Array<any>;
    const createCall = toolCalls.find((call) => call?.name === 'create_google_document');
    const result = createCall?.result || {};
    
    const documentId = result?.document_id || result?.documentId || result?.id;
    if (!documentId) {
      throw new Error('Gemini did not return a document id.');
    }
    
    const url = result?.url || `https://docs.google.com/document/d/${documentId}/edit`;
    
    return {
      id: documentId,
      name: result?.title || title || 'ClariChat Document',
      url
    };
  }
  
  private buildGoogleDocPrompt(title: string, bodyText: string): string {
    const safeTitle = title?.trim() || 'ClariChat Document';
    const safeBody = bodyText?.trim() || 'No content provided.';
    
    return [
      `Create a Google Doc titled "${safeTitle}".`,
      'Use the content below as the document body, preserving headings, lists, and code blocks when present.',
      '',
      safeBody
    ].join('\n');
  }
  
  private buildOpenAIChatRequest(
    model: string,
    prompt: string,
    history: Message[],
    extra: Record<string, unknown> = {}
  ): Record<string, unknown> {
    return {
      prompt,
      history: history.map((m: any) => ({
        role: m.role,
        content: m.content
      })),
      model,
      ...extra
    };
  }
  
  private getChatGpt54ReasoningLevel(level?: ReasoningLevel): 'instant' | 'medium' | 'high' {
    const targetLevel = level ?? this.reasoningService.getReasoningLevel();
    const mapping: Record<ReasoningLevel, 'instant' | 'medium' | 'high'> = {
      auto: 'instant',
      instant: 'instant',
      thinking: 'medium',
      pro: 'high'
    };
    return mapping[targetLevel];
  }
  
  generateMiniChatGPT54Text(prompt: string, history: any[] = []): Observable<string> {
    const body = {
      prompt,
      history
    };
    
    return this.http.post<any>(this.miniChatGPT54, body, { headers: this.getJsonHeaders() }).pipe(
      map((response) => String(response.text || '').trim()),
      catchError((error) => throwError(() => new Error(error.message || 'Failed to call Mini ChatGPT 5.4 Lambda.')))
    );
  }

  generateChatGPT54Text(prompt: string, history: any[] = [], referenceImage?: File): Observable<string> {
    return new Observable<string>((observer) => {
      const makeRequest = (imagePayload?: { base64: string; mimeType: string; dataUrl: string }) => {
        const body: any = {
          prompt,
          history
        };

        if (imagePayload) {
          body.image = imagePayload.base64;
          body.image_url = imagePayload.dataUrl;
          body.reference_image = {
            mime_type: imagePayload.mimeType,
            data: imagePayload.base64,
            data_url: imagePayload.dataUrl
          };
          body.referenceImage = {
            mimeType: imagePayload.mimeType,
            data: imagePayload.base64,
            dataUrl: imagePayload.dataUrl
          };
        }

        this.http
      .post<any>(this.chatgpt54, body, {
        headers: this.getJsonHeaders()
      })
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('Empty response from ChatGPT 5.4 Lambda.');
          }
          
          if (response.error) {
            throw new Error(response.error);
          }
          
          return String(response.text || '').trim();
        }),
        catchError((error) => {
          const message =
            error?.error?.error ||
            error?.message ||
            'Failed to call ChatGPT 5.4 Lambda.';
          return throwError(() => new Error(message));
        })
      ).subscribe({
          next: (text) => {
            observer.next(text);
            observer.complete();
          },
          error: (err) => observer.error(err)
        });
      };

      if (!referenceImage) {
        makeRequest();
        return;
      }

      this.readImagePayload(referenceImage).subscribe({
        next: (payload) => makeRequest(payload),
        error: (err) => observer.error(err)
      });
    });
  }
  
  private toObservable<T>(fn: () => ObservableInput<T>): Observable<T> {
    return defer(() => from(fn()));
  }
  
  downloadMp4FromUrl(videoUrl: string, filename = 'video.mp4'): void {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
 
  veoVideoGen(opts: VeoVideoGenOptions): Observable<VideoJobUpdate> {
    return new Observable<VideoJobUpdate>((subscriber) => {
      const validationError = this.validateOptions(opts);
      if (validationError) {
        subscriber.error(new Error(validationError));
        return undefined;
      }

      const prompt = (opts.prompt ?? '').trim();
      const uid = (opts.uid ?? '').trim();
      const sessionId = (opts.sessionId ?? '').trim();
      const seconds = opts.seconds ?? 6;
      const aspectRatio = opts.aspectRatio ?? '16:9';
      const resolution = opts.resolution ?? '720p';
      const imageBase64 = (opts.imageBase64 ?? '').trim();
      const lastFrameBase64 = (opts.lastFrameBase64 ?? '').trim();
      const modelId = (opts.modelId ?? '').trim() || undefined;

      const headers = this.requestHeaders();
      const session: VideoJobSession = { uid, sessionId };

      const startBody: VideoStartRequest = {
        action: 'start',
        prompt,
        uid,
        sessionId,
        session,
        modelId,
        duration_seconds: seconds,
        aspect_ratio: aspectRatio,
        resolution
      };

      if (imageBase64) {
        startBody.image = imageBase64;
      }
      if (lastFrameBase64) {
        startBody.lastFrameBase64 = lastFrameBase64;
      }

      let closed = false;
      let activeSub: Subscription | null = null;
      let nextPollHandle: ReturnType<typeof setTimeout> | null = null;

      const scheduleNextPoll = (jobId: string): void => {
        if (closed) {
          return;
        }
        nextPollHandle = setTimeout(() => {
          poll(jobId);
        }, this.defaultPollIntervalSeconds * 1000);
      };

      const poll = (jobId: string): void => {
        if (closed) {
          return;
        }

        const pollPayload: VideoPollRequest = {
          action: 'poll',
          id: jobId,
          session,
          max_wait_seconds: this.defaultPollMaxWaitSeconds,
          poll_interval_seconds: this.defaultPollIntervalSeconds
        };

        activeSub = this.http
          .post<VideoPollResponse>(this.veo31ClientLambdaUrl, pollPayload, { headers })
          .pipe(retryWhen((errors) => this.retryStrategy(errors)))
          .subscribe({
            next: (resp) => {
              if (closed) {
                return;
              }

              const update = this.toVideoJobUpdate(resp, jobId);
              subscriber.next(update);

              if (update.status === 'completed' || update.status === 'failed') {
                subscriber.complete();
                return;
              }

              scheduleNextPoll(jobId);
            },
            error: (err: HttpErrorResponse | Error) => {
              if (!closed) {
                subscriber.error(new Error(this.toClientMessage(err, 'Polling failed')));
              }
            }
          });
      };

      activeSub = this.http
        .post<VideoStartResponse>(this.veo31ClientLambdaUrl, startBody, { headers })
        .pipe(retryWhen((errors) => this.retryStrategy(errors)))
        .subscribe({
          next: (response) => {
            if (closed) {
              return;
            }

            const jobId = this.extractOperationName(response);
            if (!jobId) {
              subscriber.error(new Error('No job ID returned from Lambda'));
              return;
            }

            subscriber.next(this.toVideoJobUpdate(response, jobId));
            poll(jobId);
          },
          error: (err: HttpErrorResponse | Error) => {
            if (!closed) {
              subscriber.error(new Error(this.toClientMessage(err, 'Start failed')));
            }
          }
        });

      return () => {
        closed = true;
        if (nextPollHandle) {
          clearTimeout(nextPollHandle);
          nextPollHandle = null;
        }
        activeSub?.unsubscribe();
      };
    });
  }

  private extractOperationName(response: VideoStartResponse | VideoPollResponse | null | undefined): string | null {
    const id = String(response?.id ?? '').trim();
    if (id) {
      return id;
    }

    const operationName = String(response?.operation_name ?? '').trim();
    return operationName || null;
  }

  private normalizeStatus(rawStatus: string | null | undefined): VideoJobStatus {
    const value = String(rawStatus ?? '').trim().toLowerCase();

    if (value === 'completed' || value === 'succeeded' || value === 'done') {
      return 'completed';
    }
    if (value === 'failed' || value === 'error' || value === 'cancelled' || value === 'canceled') {
      return 'failed';
    }
    if (value === 'queued' || value === 'pending' || value === 'not_started') {
      return 'queued';
    }

    return 'running';
  }

  private toVideoJobUpdate(
    response: VideoStartResponse | VideoPollResponse | null | undefined,
    fallbackId: string
  ): VideoJobUpdate {
    const id = this.extractOperationName(response) ?? fallbackId;
    const url = response?.url ?? response?.video_uri ?? null;

    return {
      id,
      operation_name: response?.operation_name ?? id,
      status: this.normalizeStatus(response?.status),
      url,
      videoUrl: url,
      error: response?.error ?? null,
      session: response?.session,
      modelId: response?.modelId,
      imageModel: response?.imageModel
    };
  }

  private validateOptions(opts: VeoVideoGenOptions): string | null {
    const prompt = (opts.prompt ?? '').trim();
    const uid = (opts.uid ?? '').trim();
    const sessionId = (opts.sessionId ?? '').trim();
    const seconds = opts.seconds ?? 6;
    const aspectRatio = opts.aspectRatio ?? '16:9';
    const resolution = opts.resolution ?? '720p';
    const imageBase64 = (opts.imageBase64 ?? '').trim();
    const lastFrameBase64 = (opts.lastFrameBase64 ?? '').trim();

    if (!prompt && !imageBase64) {
      return 'Either prompt or imageBase64 is required';
    }
    if (!uid || !sessionId) {
      return 'uid and sessionId are required';
    }
    if (![4, 6, 8].includes(seconds)) {
      return 'seconds must be 4, 6, or 8';
    }
    if (aspectRatio !== '16:9' && aspectRatio !== '9:16') {
      return "aspectRatio must be '16:9' or '9:16'";
    }
    if (resolution !== '720p' && resolution !== '1080p') {
      return "resolution must be '720p' or '1080p'";
    }
    if (resolution === '1080p' && seconds !== 8) {
      return 'Veo 3.1 requires seconds=8 when resolution is 1080p';
    }
    if (lastFrameBase64 && !imageBase64) {
      return 'lastFrameBase64 requires imageBase64 for first-and-last-frame generation';
    }

    return null;
  }

  private requestHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  private toClientMessage(error: any, fallbackMessage: string): string {
    const httpError = error as HttpErrorResponse;
    const payload = httpError?.error;

    const payloadError = payload && typeof payload === 'object' ? (payload as any).error : null;

    const upstreamMessage =
      (typeof payloadError === 'string' ? payloadError : null) ||
      (payloadError && typeof payloadError === 'object' ? (payloadError as any).message : null) ||
      (payload && typeof payload === 'object' ? (payload as any).message : null) ||
      (payload && typeof payload === 'object' && (payload as any).details?.error
        ? (payload as any).details.error.message
        : null) ||
      httpError?.message ||
      fallbackMessage;

    const upstreamStatus =
      (payload && typeof payload === 'object' ? (payload as any).status : null) ||
      (payload && typeof payload === 'object' && (payload as any).details?.error
        ? (payload as any).details.error.status
        : null) ||
      (payload && typeof payload === 'object' ? (payload as any).details?.status : null) ||
      '';

    const upstreamCode =
      (payload && typeof payload === 'object' ? (payload as any).code : null) ||
      (payload && typeof payload === 'object' && (payload as any).details?.error
        ? (payload as any).details.error.code
        : null) ||
      httpError?.status ||
      0;

    if (upstreamCode === 400 || upstreamStatus === 'INVALID_ARGUMENT') {
      return `Veo request invalid: ${upstreamMessage}`;
    }
    if (upstreamCode === 401 || upstreamCode === 403) {
      return `Veo authorization failed: ${upstreamMessage}`;
    }
    if (upstreamCode === 429) {
      return `Veo rate limit reached: ${upstreamMessage}`;
    }
    if (upstreamCode >= 500) {
      return `Veo upstream service error: ${upstreamMessage}`;
    }

    return String(upstreamMessage || fallbackMessage);
  }

  private retryStrategy(errors: Observable<HttpErrorResponse>): Observable<number> {
    const maxRetries = 2;

    return errors.pipe(
      mergeMap((error: HttpErrorResponse, index: number) => {
        const attempt = index + 1;
        const status = error?.status ?? 0;
        const isRetryable = status === 0 || status === 429 || status >= 500;

        if (attempt > maxRetries || !isRetryable) {
          return throwError(() => error);
        }

        return timer(attempt * 1000);
      })
    );
  }
  // =========================================================
  // ===================== CogVideoX-3 =======================
  // =========================================================
  /** 
   * Calls the CogVideoX Lambda: single POST; Lambda polls Z.AI internally and returns a job update
   * shaped like { id, status, progress, videoUrl, thumbnailUrl, error? } (see lambda_function.py).
   * Request body matches Lambda: prompt, uid, sessionId (required); optional image_url / legacy image,
   * quality, with_audio, size, fps, duration, request_id.
   */
  cogVideoX3Gen(opts: {
    prompt: string;
    uid: string;
    sessionId: string;
    imageFile?: File;
    quality?: 'quality' | 'speed';
    withAudio?: boolean;
    size?: string;
    fps?: number;
    duration?: 5 | 10;
    requestId?: string;
  }): Observable<VideoJobUpdate> {
    const out$ = new Subject<VideoJobUpdate>();
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    const unwrapLambdaHttpBody = (raw: any): any => {
      if (raw == null) return {};
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw);
        } catch {
          return {};
        }
      }
      const code = raw.statusCode ?? raw.status_code;
      if (typeof code === 'number' && raw.body != null) {
        const b = raw.body;
        if (typeof b === 'string') {
          try {
            return JSON.parse(b);
          } catch {
            return { error: b };
          }
        }
        if (typeof b === 'object') return b;
      }
      if (typeof raw.body === 'string' && raw.body.trim()) {
        try {
          return { ...raw, ...JSON.parse(raw.body) };
        } catch {
          return raw;
        }
      }
      return raw;
    };

    const mapLambdaJobToUpdate = (row: any): VideoJobUpdate => {
      const st = String(row?.status || '').toLowerCase().trim();
      const status: VideoJobStatus =
        st === 'completed' ? 'completed' : st === 'failed' ? 'failed' : 'running';
      const id = String(row?.id || 'unknown').trim() || 'unknown';
      const progress =
        typeof row?.progress === 'number'
          ? row.progress
          : status === 'completed'
            ? 100
            : status === 'failed'
              ? 0
              : 50;
      return {
        id,
        status,
        progress,
        url: row?.videoUrl ?? row?.video_url ?? null,
        videoUrl: row?.videoUrl ?? row?.video_url ?? null,
        thumbnailUrl: row?.thumbnailUrl ?? row?.thumbnail_url ?? null,
        error: typeof row?.error === 'string' ? row.error : row?.error != null ? String(row.error) : null
      };
    };

    const readFileAsDataUrl = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Could not read image file'));
        reader.readAsDataURL(file);
      });

    const run = async (): Promise<void> => {
      const body: Record<string, any> = {
        prompt: opts.prompt,
        uid: opts.uid,
        sessionId: opts.sessionId
      };

      if (opts.requestId) {
        body.request_id = opts.requestId;
      }
      if (opts.quality) {
        body.quality = opts.quality;
      }
      if (opts.withAudio !== undefined) {
        body.with_audio = opts.withAudio;
      }
      if (opts.size) {
        body.size = opts.size;
      }
      if (opts.fps != null) {
        body.fps = opts.fps;
      }
      if (opts.duration != null) {
        body.duration = opts.duration;
      }

      if (opts.imageFile) {
        try {
          const dataUrl = await readFileAsDataUrl(opts.imageFile);
          const base64 = dataUrl.includes(',') ? (dataUrl.split(',')[1] || '').trim() : dataUrl.trim();
          body.image_url = [base64];
        } catch (e: any) {
          out$.next({
            id: 'unknown',
            status: 'failed',
            progress: 0,
            url: null,
            videoUrl: null,
            error: e?.message || 'Could not read image file'
          });
          out$.complete();
          return;
        }
      }

      this.http.post<any>(this.cogxLambdaUrl, body, { headers }).subscribe({
        next: (raw) => {
          const row = unwrapLambdaHttpBody(raw);
          if (row?.error != null && row?.status == null) {
            out$.next({
              id: String(row?.id || row?.requestId || 'unknown'),
              status: 'failed',
              progress: 0,
              url: null,
              videoUrl: null,
              error: typeof row.error === 'string' ? row.error : JSON.stringify(row.error)
            });
            out$.complete();
            return;
          }
          let update = mapLambdaJobToUpdate(row);
          if (update.status === 'running' && !update.videoUrl) {
            update = {
              ...update,
              status: 'failed',
              progress: 0,
              error:
                update.error ||
                'Video did not finish within the server wait window. Try again or shorten the prompt.'
            };
          }
          out$.next(update);
          out$.complete();
        },
        error: (err) => {
          const payload = unwrapLambdaHttpBody(err?.error);
          const msg =
            (typeof payload?.error === 'string' && payload.error) ||
            err?.error?.error ||
            err?.message ||
            'CogVideoX request failed';
          out$.next({ id: 'unknown', status: 'failed', progress: 0, url: null, videoUrl: null, error: msg });
          out$.complete();
        }
      });
    };

    // Lambda uses progress 50 while processing; mirror that until the HTTP call returns.
    out$.next({ id: 'pending', status: 'running', progress: 50, url: null, videoUrl: null, error: null });

    void run();

    return out$.asObservable();
  }

  /**
   * Nova Reel 1.1 Lambda flow:
   * - Single request to Lambda (server handles Bedrock async start + polling via get_async_invoke)
   * - Response expected as API Gateway/Lambda proxy shape:
   *   { statusCode, body: "{\"videoUrl\":\"...\"}" } or { statusCode, body: "{\"error\":\"...\"}" }
   */
  novaReel11Gen(opts: {
    prompt: string;
    uid: string;
    sessionId: string;
    imageFile?: File;
    requestId?: string;
  }): Observable<VideoJobUpdate> {
    const out$ = new Subject<VideoJobUpdate>();
    const endpoint = this.novaReel11LambdaUrl.replace(/\/+$/, '');

    if (!endpoint) {
      out$.next({
        id: 'unknown',
        status: 'failed',
        progress: 0,
        url: null,
        videoUrl: null,
        error: 'Missing Nova Reel 1.1 endpoint configuration.'
      });
      out$.complete();
      return out$.asObservable();
    }

    const parseEnvelope = (raw: any): { statusCode: number; payload: any } => {
      let data = raw;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return { statusCode: 500, payload: { error: data } };
        }
      }

      const statusCode =
        typeof data?.statusCode === 'number'
          ? data.statusCode
          : typeof data?.status_code === 'number'
            ? data.status_code
            : 200;

      let payload: any = data;
      if (data?.body != null) {
        if (typeof data.body === 'string') {
          try {
            payload = JSON.parse(data.body);
          } catch {
            payload = { error: data.body };
          }
        } else if (typeof data.body === 'object') {
          payload = data.body;
        }
      }

      return { statusCode, payload };
    };

    const run = async (): Promise<void> => {
      const body: Record<string, any> = {
        text: String(opts.prompt || '').trim()
      };

      if (opts.requestId) {
        body.requestId = opts.requestId;
      }

      if (opts.imageFile) {
        try {
          const payload = await firstValueFrom(this.readImagePayload(opts.imageFile));
          body.imageBase64 = payload.base64;
          body.imageType = payload.mimeType === 'image/png' ? 'png' : 'jpeg';
        } catch (err: any) {
          out$.next({
            id: opts.requestId || 'unknown',
            status: 'failed',
            progress: 0,
            url: null,
            videoUrl: null,
            error: err?.message || 'Could not read reference image.'
          });
          out$.complete();
          return;
        }
      }

      this.http
        .post<any>(endpoint, body, {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        })
        .subscribe({
          next: (raw) => {
            const { statusCode, payload } = parseEnvelope(raw);
            const videoUrl = String(payload?.videoUrl || payload?.video_url || '').trim();
            const errorMsg = String(payload?.error || payload?.message || '').trim();

            if (statusCode >= 400 || !videoUrl) {
              out$.next({
                id: opts.requestId || 'unknown',
                status: 'failed',
                progress: 0,
                url: null,
                videoUrl: null,
                error: errorMsg || 'Nova Reel 1.1 generation failed.'
              });
              out$.complete();
              return;
            }

            out$.next({
              id: opts.requestId || 'unknown',
              status: 'completed',
              progress: 100,
              url: videoUrl,
              videoUrl,
              error: null
            });
            out$.complete();
          },
          error: (err) => {
            out$.next({
              id: opts.requestId || 'unknown',
              status: 'failed',
              progress: 0,
              url: null,
              videoUrl: null,
              error: err?.error?.error || err?.message || 'Nova Reel 1.1 request failed.'
            });
            out$.complete();
          }
        });
    };

    // Server performs async polling internally; client only shows queued/progress until response returns.
    out$.next({ id: opts.requestId || 'pending', status: 'queued', progress: 10, url: null, videoUrl: null, error: null });
    void run();

    return out$.asObservable();
  }

  getGLM5Chat(
  prompt: string,
  history: Message[] = [],
  opts?: {
    max_tokens?: number;
    temperature?: number;
    top_p?: number;
    auto_continue?: boolean;
    request_id?: string;
  }
): Observable<string> {
  const cleanPrompt = String(prompt ?? '').trim();
  if (!cleanPrompt) {
    return throwError(() => new Error('Prompt cannot be empty.'));
  }

  const payload: any = {
    prompt: cleanPrompt,
    history: this.sanitizeHistoryForApi(history)
  };

  void opts;

  return this.http.post<any>(this.glm5Chat, payload, {
    headers: this.getJsonHeaders()
  }).pipe(
    map((resp) => {
      const text = String(resp?.reply ?? resp?.text ?? '').trim();
      if (!text) {
        throw new Error(String(resp?.error || 'No response text received from GLM-5.'));
      }
      return text;
    }),
    catchError((error) => {
      this.errorLogger.logError(error, 'AI: GLM-5 Chat', this.getCurrentUserId());
      const message =
        error?.error?.error ||
        error?.error?.message ||
        error?.message ||
        'GLM-5 request failed.';
      return throwError(() => new Error(String(message)));
    })
  );
}

  chatWithQwenCoder(
    prompt: string,
    history: QwenCoderChatMessage[] = [],
    options: {
      maxTokens?: number;
      temperature?: number;
      topP?: number;
    } = {}
  ): Observable<QwenCoderChatResponse> {
    const cleanPrompt = String(prompt ?? '').trim();
    if (!cleanPrompt) {
      return throwError(() => new Error('Prompt cannot be empty.'));
    }

    const payload = {
      prompt: cleanPrompt,
      history: (history ?? []).map((msg) => ({
        role: msg?.role === 'assistant' ? 'assistant' : 'user',
        content: String(msg?.content ?? '')
      })),
      maxTokens: options.maxTokens ?? 4096,
      temperature: options.temperature ?? 0.1,
      topP: options.topP ?? 0.9
    };

    return this.http.post<QwenCoderChatResponse>(
      this.qwenChat,
      payload,
      { headers: this.getJsonHeaders() }
    ).pipe(
      retry(1),
      map((response) => ({
        reply: String(response?.reply ?? response?.text ?? response?.model_output ?? '').trim(),
        text: response?.text,
        model_output: response?.model_output,
        history: Array.isArray(response?.history)
          ? response.history.map((item) => ({
              role: (item?.role === 'assistant' ? 'assistant' : 'user') as 'user' | 'assistant',
              content: String(item?.content ?? '')
            }))
          : [],
        modelId: response?.modelId,
        requestId: response?.requestId,
        stopReason: response?.stopReason,
        usage: response?.usage ?? {}
      })),
      catchError((error: HttpErrorResponse) => {
        this.errorLogger.logError(error, 'AI: Qwen Coder Chat', this.getCurrentUserId());
        const serverMessage = error?.error?.error || error?.error?.message || error?.message || 'Qwen Coder chat request failed';
        return throwError(() => new Error(String(serverMessage)));
      })
    );
  }

  askQwen(history: any[], prompt: string): Observable<{ reply: string; history: any[] }> {
    const normalizedHistory = this.sanitizeHistoryForApi((history as Message[]) || []).map((msg) => ({
      role: msg.role === 'assistant' ? 'assistant' : 'user',
      content: String(msg.content ?? '')
    })) as QwenCoderChatMessage[];
    return this.chatWithQwenCoder(prompt, normalizedHistory).pipe(
      map((resp) => ({
        reply: String(resp?.reply ?? '').trim(),
        history: Array.isArray(resp?.history) ? resp.history : []
      }))
    );
  }

  generateQwenCoderText(prompt: string, history: Message[] = []): Observable<string> {
    const normalizedHistory = this.sanitizeHistoryForApi(history).map((msg) => ({
      role: msg.role === 'assistant' ? 'assistant' : 'user',
      content: String(msg.content ?? '')
    })) as QwenCoderChatMessage[];

    return this.chatWithQwenCoder(prompt, normalizedHistory).pipe(
      map((resp) => {
        const text = String(resp?.reply ?? resp?.text ?? resp?.model_output ?? '').trim();
        if (!text) {
          throw new Error('No response text received from Qwen Coder.');
        }
        return text;
      })
    );
  }

  generateQwenText(prompt: string, history: Message[] = []): Observable<string> {
    return this.generateQwenCoderText(prompt, history);
  }
  
  // =========================================================
  // ===================== Z.ai (GLM) ========================
  // =========================================================
  generateZaiChatText(
    prompt: string,
    history: Message[] = []
  ): Observable<string> {
    return this.toObservable(async () => {
      const cleanPrompt = String(prompt ?? '').trim();
      if (!cleanPrompt) throw new Error('Prompt cannot be empty.');
      
      if (!this.zaiApikey) {
        throw new Error('Missing GLM_API_KEY in environment configuration.');
      }
      
      const messages = [...this.sanitizeHistoryForApi(history)];
      if (!messages.length || messages[messages.length - 1]?.content !== cleanPrompt) {
        messages.push({ role: 'user', content: cleanPrompt });
      }
      
      const body: any = {
        model: 'glm-4.7',
        messages,
        stream: false,
        temperature: 1.0,
        top_p: 0.95,
        max_tokens: 2048
      };
      
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.zaiApikey}`
      });
      
      try {
        const resp = await firstValueFrom(
          this.http.post<any>(this.zaiChatCompletionsUrl, body, { headers })
        );
        const text = this.extractZaiText(resp);
        return text || 'Sorry, no response text received from GLM.';
      } catch (error: any) {
        this.errorLogger.logError(error, 'AI: GLM Chat', this.getCurrentUserId());
        throw error;
      }
    });
  }
  
  // =========================================================
  // ============== OpenAI Responses: text helpers ============
  // =========================================================
  private buildInput(history: Message[] = [], prompt = ''): any[] {
    const input: any[] = [];
    for (const m of history || []) {
      const c = (m as any)?.content;
      if (typeof c !== 'string' || !c.trim()) continue;
      input.push({ role: (m as any).role, content: c });
    }
    input.push({ role: 'user', content: String(prompt ?? '').trim() });
    return input;
  }
  
  private parseOpenAIResponse(r: any): { text: string; citations: string[] } {
    let text = (r?.output_text ?? '').toString().trim();
    
    if (!text && Array.isArray(r?.output)) {
      const parts: string[] = [];
      for (const block of r.output) {
        const content = Array.isArray(block?.content) ? block.content : [];
        for (const c of content) {
          if ((c?.type === 'output_text' || c?.type === 'text') && typeof c?.text === 'string') parts.push(c.text);
          else if (c?.type === 'message' && Array.isArray(c?.content)) {
            for (const cc of c.content) if (typeof cc?.text === 'string') parts.push(cc.text);
          }
        }
      }
      text = parts.join('\n\n').trim();
    }
    
    if (!text && Array.isArray(r?.choices)) {
      const c = r.choices?.[0]?.message?.content;
      if (typeof c === 'string') text = c.trim();
    }
    
    const urls = new Set<string>();
    const pushUrl = (u: any) => {
      if (typeof u === 'string' && /^https?:\/\//i.test(u)) urls.add(u);
    };
    
    const walk = (obj: any) => {
      if (!obj) return;
      if (Array.isArray(obj)) { obj.forEach(walk); return; }
      if (typeof obj !== 'object') return;
      for (const k of Object.keys(obj)) {
        const v = obj[k];
        if (k === 'citations' && Array.isArray(v)) v.forEach(pushUrl);
        if (k === 'url') pushUrl(v);
        walk(v);
      }
    };
    
    if (Array.isArray(r?.citations)) r.citations.forEach(pushUrl);
    if (Array.isArray(r?.output)) walk(r.output);
    if (Array.isArray(r?.tools)) walk(r.tools);
    
    return { text: text || '', citations: Array.from(urls) };
  }
  
  getChatGPT54Chat(prompt: string, history: Message[] = []): Observable<string> {
  const cleanPrompt = String(prompt || '').trim();
  if (!cleanPrompt) {
    return throwError(() => new Error('Prompt cannot be empty.'));
  }

  const payload = {
    prompt: cleanPrompt,
    history: (history || [])
      .map((msg: any) => ({
        role: String(msg?.role || 'user').trim().toLowerCase() === 'assistant' ? 'assistant' : 'user',
        content: String(msg?.content ?? msg?.text ?? msg?.message ?? '').trim()
      }))
      .filter((msg: any) => !!msg.content)
  };

  return this.http.post<any>(this.openAliEndpoint, payload, {
    headers: { 'Content-Type': 'application/json' }
  }).pipe(
    map((resp) => String(resp?.text ?? '').trim()),
    catchError((error: any) => {
      const message =
        error?.error?.error ||
        error?.error?.message ||
        error?.message ||
        'ChatGPT 5.4 request failed.';
      return throwError(() => new Error(String(message)));
    })
  );
}

  
  // =========================================================
  // ============== Other model helpers ======================
  // =========================================================
  generateText(prompt: string, history: Message[]): Observable<string> {
    const payload = { prompt, history: history.map((msg: any) => ({ role: msg.role, content: msg.content })) };
    return this.http.post<any>(this.ClaudeChat, payload).pipe(map((resp) => resp.text));
  }
  
  generateJambaLargeText(prompt: string, history: Message[], length?: AnswerLength): Observable<string> {
    const payload = {
      prompt,
      history: history.map((msg: any) => ({ role: msg.role, content: msg.content })),
      length
    };
    return this.http.post<any>(this.jambaChat, payload).pipe(
      map((resp) => (typeof resp?.text === 'string' ? resp.text : (resp?.output ?? '')))
    );
  }
  
  generateJambaText(prompt: string, history: Message[], length?: AnswerLength): Observable<string> {
    return this.generateJambaLargeText(prompt, history, length);
  }
  
  generatePalmXText(prompt: string, history: Message[]): Observable<string> {
    const body = {
      prompt: String(prompt ?? '').trim(),
      history: this.sanitizeHistoryForApi(history)
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post<{ text: string }>(this.palmxChat, body, { headers })
      .pipe(map((r) => (r && typeof r.text === 'string' ? r.text : '')));
  }
  
  generateGeminiText(
    prompt: string,
    history: Message[],
    image?: File,
    thinkingLevel: 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH' = 'MINIMAL',
    mediaResolution: 'low' | 'medium' | 'high' | 'ultra_high' = 'high'
  ): Observable<string> {
    return new Observable<string>((observer) => {
      const makeRequest = (imageData?: string, mimeType?: string) => {
        const level = (thinkingLevel || 'MINIMAL').toUpperCase();
        const reasoning =
          level === 'HIGH' ? 'high' :
          level === 'MEDIUM' ? 'medium' :
          level === 'LOW' ? 'low' :
          'instant';
        
        const requestBody: any = {
          prompt,
          history: history.map((m: any) => ({
            role: m.role,
            content: m.content
          })),
          reasoning
        };
        
        if (imageData) {
          requestBody.image = imageData;
          requestBody.image_url = `data:${mimeType || 'image/png'};base64,${imageData}`;
          requestBody.reference_image = {
            inline_data: {
              mime_type: mimeType || 'image/png',
              data: imageData
            }
          };
          requestBody.referenceImage = {
            inlineData: {
              mimeType: mimeType || 'image/png',
              data: imageData
            }
          };
        }
        
        void mimeType;
        void mediaResolution;
        
        this.http
          .post<any>(this.geminiUrl, requestBody, {
            headers: { 'Content-Type': 'application/json' }
          })
          .pipe(
            map((r) => r.text ?? 'Sorry, I cannot respond to that query.'),
            catchError((err) => {
              observer.error(err);
              return throwError(() => err);
            })
          )
          .subscribe({
            next: (text) => {
              observer.next(text);
              observer.complete();
            },
            error: (err) => {
              observer.error(err);
            }
          });
      };
      
      if (image) {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!reader.result) {
            observer.error('Could not read image file');
            return;
          }
          const base64Image = (reader.result as string).split(',')[1];
          makeRequest(base64Image);
        };
        reader.onerror = () => {
          observer.error('Error reading image file');
        };
        reader.readAsDataURL(image);
      } else {
        makeRequest();
      }
    });
  }
  
  private extractText(resp: any): string {
    if (!resp) return '';
    if (typeof resp.text === 'string') return resp.text;
    if (typeof resp.assistant === 'string') return resp.assistant;
    if (typeof resp.model_output === 'string') return resp.model_output;
    if (resp.model_output && typeof resp.model_output === 'object' && typeof resp.model_output.text === 'string') {
      return resp.model_output.text;
    }
    if (resp.output?.text && typeof resp.output.text === 'string') return resp.output.text;
    if (resp.data?.text && typeof resp.data.text === 'string') return resp.data.text;
    return '';
  }

  /**
   * Lambda Function URL may return the JSON body directly, or an API-Gateway-style envelope
   * with a stringified JSON `body` (see server `_success` / `_parse_body`).
   */
  private unwrapLambdaFunctionUrlJsonBody(raw: any): any {
    if (raw == null) {
      return {};
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    }
    if (typeof raw.body === 'string') {
      try {
        return JSON.parse(raw.body);
      } catch {
        return raw;
      }
    }
    if (raw.body && typeof raw.body === 'object') {
      return raw.body;
    }
    return raw;
  }

  /** Bedrock Converse Lambda success payload uses top-level `text` (see server `extract_response`). */
  private extractOpus46ConverseLambdaText(raw: any): string {
    const r = this.unwrapLambdaFunctionUrlJsonBody(raw);
    if (typeof r.text === 'string') {
      return r.text;
    }
    if (r.error) {
      throw new Error(String(r.error));
    }
    return this.extractText(r);
  }

  /**
   * Matches Bedrock Converse `build_conversation`: only user/assistant, string content.
   * Server caps turns; we send at most the last 200 messages (100 turns × 2).
   */
  private normalizeBedrockConverseHistory(history: Message[]): Array<{ role: string; content: string }> {
    const valid = new Set(['user', 'assistant']);
    const h = Array.isArray(history) ? history : [];
    const rows: Array<{ role: string; content: string }> = [];
    for (const msg of h) {
      const role = String(msg?.role || '').toLowerCase();
      if (!valid.has(role)) {
        continue;
      }
      const content =
        typeof msg?.content === 'string'
          ? msg.content.trim()
          : String((msg as any)?.content?.text ?? msg?.content ?? '').trim();
      if (!content) {
        continue;
      }
      rows.push({ role, content });
    }
    return rows.slice(-200);
  }

  /**
   * Optional `inferenceConfig` for Opus 4.6 Lambda — camelCase keys per server `InferenceConfig.to_dict`.
   * Omits both temperature and topP together in a way that matches server: if both set, drop topP.
   */
  private sanitizeBedrockInferenceConfig(raw?: {
    maxTokens?: number;
    temperature?: number;
    topP?: number;
    stopSequences?: string[];
  }): Record<string, unknown> | null {
    if (!raw) {
      return null;
    }
    const out: Record<string, unknown> = {};
    if (raw.maxTokens != null && Number.isFinite(Number(raw.maxTokens))) {
      out.maxTokens = Math.max(1, Math.min(10000, Math.floor(Number(raw.maxTokens))));
    }
    let temperature: number | undefined;
    if (raw.temperature != null && Number.isFinite(Number(raw.temperature))) {
      temperature = Math.max(0, Math.min(1, Number(raw.temperature)));
      out.temperature = temperature;
    }
    if (raw.topP != null && Number.isFinite(Number(raw.topP))) {
      if (temperature !== undefined) {
        // Server drops topP when both are supplied — mirror that to avoid validation issues.
      } else {
        out.topP = Math.max(0, Math.min(1, Number(raw.topP)));
      }
    }
    if (Array.isArray(raw.stopSequences) && raw.stopSequences.length) {
      const seq = raw.stopSequences.map((s) => String(s).trim()).filter(Boolean);
      if (seq.length) {
        out.stopSequences = seq;
      }
    }
    return Object.keys(out).length ? out : null;
  }
  
  private normalizeHistory(history: any[]): Array<{ role: string; content: string }> {
    const h = Array.isArray(history) ? history : [];
    return h.slice(-10).map((msg: any) => ({
      role: msg?.role,
      content:
        typeof msg?.content === 'string'
          ? msg.content
          : (msg?.content?.text ?? JSON.stringify(msg?.content ?? ''))
    }));
  }
  
  private handleHttpError(err: HttpErrorResponse) {
    const status = err?.status;
    const body = err?.error;
    
    let msg = '';
    if (typeof body === 'string') msg = body;
    else if (body && typeof body === 'object') msg = body.error || body.message || body.Message || JSON.stringify(body);
    else msg = err?.message || 'Request failed';
    
    return throwError(() => new Error(`[${status}] ${msg}`));
  }
  
  /**
   * Claude Opus 4.6 via Bedrock Converse Lambda.
   * Request body matches server `lambda_handler`: `prompt`, `history` (or `messages`),
   * optional `systemPrompt`, optional `inferenceConfig` with camelCase keys.
   */
  generateOpyDopy6(
    prompt: string,
    history: Message[],
    opts?: {
      systemPrompt?: string;
      inferenceConfig?: {
        maxTokens?: number;
        temperature?: number;
        topP?: number;
        stopSequences?: string[];
      };
    }
  ): Observable<string> {
    const trimmed = (prompt ?? '').trim();
    if (!trimmed) {
      return throwError(() => new Error("Missing or empty 'prompt' in request body"));
    }

    const payload: Record<string, unknown> = {
      prompt: trimmed,
      history: this.normalizeBedrockConverseHistory(history)
    };

    const sys = opts?.systemPrompt?.trim();
    if (sys) {
      payload.systemPrompt = sys;
    }

    const ic = this.sanitizeBedrockInferenceConfig(opts?.inferenceConfig);
    if (ic) {
      payload.inferenceConfig = ic;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<any>(this.opus46BedrockConverseLambdaUrl, payload, { headers }).pipe(
      map((resp) => this.extractOpus46ConverseLambdaText(resp)),
      catchError((e) => this.handleHttpError(e))
    );
  }

  /**
   * Claude Opus 4.7 — Bedrock Converse Lambda (same contract as Opus 4.6).
   * Sends `catalogModelId: 'opus47'` so the handler can route to the 4.7 profile when supported.
   */
  generateOpus47Text(
    prompt: string,
    history: Message[],
    opts?: {
      systemPrompt?: string;
      inferenceConfig?: {
        maxTokens?: number;
        temperature?: number;
        topP?: number;
        stopSequences?: string[];
      };
    }
  ): Observable<string> {
    const trimmed = (prompt ?? '').trim();
    if (!trimmed) {
      return throwError(() => new Error("Missing or empty 'prompt' in request body"));
    }

    const payload: Record<string, unknown> = {
      prompt: trimmed,
      history: this.normalizeBedrockConverseHistory(history),
      catalogModelId: 'opus47'
    };

    const sys = opts?.systemPrompt?.trim();
    if (sys) {
      payload.systemPrompt = sys;
    }

    const ic = this.sanitizeBedrockInferenceConfig(opts?.inferenceConfig);
    if (ic) {
      payload.inferenceConfig = ic;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<any>(this.opus47BedrockConverseLambdaUrl, payload, { headers }).pipe(
      map((resp) => this.extractOpus46ConverseLambdaText(resp)),
      catchError((e) => this.handleHttpError(e))
    );
  }

  /** Kimi K2.5 (Moonshot AI) via dedicated Lambda Function URL. */
  generateKimiK25Text(prompt: string, history: Message[]): Observable<string> {
    const payload = {
      prompt: prompt ?? '',
      history: history.map((msg: any) => ({ role: msg.role, content: msg.content }))
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(this.kimiK25ChatUrl, payload, { headers }).pipe(
      map((raw) => {
        const r = this.unwrapLambdaFunctionUrlJsonBody(raw);
        if (typeof r.text === 'string') {
          return r.text.trim();
        }
        if (r.error) {
          throw new Error(String(r.error));
        }
        return String(this.extractText(r) ?? '').trim();
      }),
      catchError((e) => this.handleHttpError(e))
    );
  }

  /** Older Opus chat lambda (fallback when Claude Opus 4.6 fails). */
  generateOpusLegacyText(prompt: string, history: Message[]): Observable<string> {
    const payload = {
      prompt: prompt ?? '',
      history: this.normalizeHistory(history)
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(this.opusChat, payload, { headers }).pipe(
      map((resp) => this.extractText(resp)),
      catchError((e) => this.handleHttpError(e))
    );
  }

  generateOpus45Text(prompt: string, history: Message[]): Observable<string> {
    // Backward-compatible alias used by existing components.
    return this.generateOpyDopy6(prompt, history);
  }

  generateOpus37Text(prompt: string, history: Message[]): Observable<string> {
    const payload = {
      prompt: prompt ?? '',
      history: this.normalizeHistory(history)
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(this.opus37Chat, payload, { headers }).pipe(
      map((resp) => this.extractText(resp)),
      catchError((e) => this.handleHttpError(e))
    );
  }

  generateSonnet45Text(prompt: string, history: Message[]): Observable<string> {
    const payload = { prompt, history: history.map((msg: any) => ({ role: msg.role, content: msg.content })) };
    return this.http.post<any>(this.sonnet45Chat, payload).pipe(map((resp) => resp.text));
  }
  
  generateHaiku45Text(prompt: string, history: Message[]): Observable<string> {
    const payload = {
      prompt,
      history: history.map((msg: any) => ({ role: msg.role, content: msg.content }))
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(this.haiku45Chat, payload, { headers }).pipe(
      map((resp) => this.extractText(resp)),
      catchError((e) => this.handleHttpError(e))
    );
  }
  
  private buildChatUrl(base: string): string {
    const b = (base || '').trim();
    if (!b) return '';
    const noTrail = b.replace(/\/+$/, '');
    if (/(\/v1\/chat)$/.test(noTrail)) return noTrail;
    return `${noTrail}/v1/chat`;
  }
  
  generateMistralLargeText(prompt: string, history: Message[], opts?: { instructprompt?: string }): Observable<string> {
    const payload = {
      prompt: prompt ?? '',
      history: this.normalizeHistory(history),
      instructprompt: opts?.instructprompt || undefined
    };
    
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    
    return this.http.post<any>(this.mistralLargeChat, payload, { headers }).pipe(
      map((resp) => this.extractText(resp)),
      catchError((e) => this.handleHttpError(e))
    );
  }
  
  generateCodestraltoText(prompt: string, history: Message[]): Observable<string> {
    const payload = {
      prompt: prompt ?? '',
      history: this.normalizeHistory(history)
    };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(this.codestraltoChat, payload, { headers }).pipe(
      map((resp) => this.extractText(resp)),
      catchError((e) => this.handleHttpError(e))
    );
  }
  
  generateLlamaText(prompt: string, history: Message[]): Observable<string> {
    const payload = { prompt, history: history.map((msg: any) => ({ role: msg.role, content: msg.content })) };
    return this.http.post<any>(this.LlamaChat, payload).pipe(map((resp) => resp.summary));
  }

  getPegasus(request: Omit<PegasusLambdaRequest, 'modelId'>): Observable<PegasusLambdaResponse> {
    return this.http.post<PegasusLambdaResponse>(this.pegasusLambdaUrl, {
      modelId: PEGASUS_MODEL_ID,
      inputPrompt: request.inputPrompt,
      mediaSource: request.mediaSource,
      temperature: request.temperature,
      maxOutputTokens: request.maxOutputTokens,
      responseFormat: request.responseFormat,
      history: request.history ?? []
    });
  }

  generatePegasusText(
    prompt: string,
    history: Message[],
    image?: File
  ): Observable<string> {
    if (!image) {
      return throwError(() => new Error('Pegasus requires a reference image.'));
    }
    return defer(() => from(this.readFileAsRawBase64(image))).pipe(
      switchMap((base64String) =>
        this.getPegasus({
          inputPrompt: prompt ?? '',
          mediaSource: { base64String },
          history: this.normalizeHistory(history).map((m) => ({
            role: m.role === 'assistant' ? 'assistant' : 'user',
            content: m.content
          }))
        })
      ),
      map((resp) => String(resp?.text ?? resp?.model_output ?? '').trim()),
      catchError((e) => this.handleHttpError(e as HttpErrorResponse))
    );
  }

  private readFileAsRawBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = String(reader.result || '');
        if (!dataUrl) {
          reject(new Error('Could not read image'));
          return;
        }
        const base64 = dataUrl.includes(',') ? dataUrl.split(',', 2)[1]!.trim() : dataUrl.trim();
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Could not read image'));
      reader.readAsDataURL(file);
    });
  }
  
  generateMaverickText(prompt: string, history: Message[]): Observable<string> {
    const payload = { prompt, history: history.map((msg: any) => ({ role: msg.role, content: msg.content })) };
    return this.http.post<any>(this.maverickChat, payload).pipe(map((resp) => resp.text));
  }
  
  generateNova2Text(
    prompt: string,
    history: Message[],
    image?: File
  ): Observable<{ text: string; image?: string }> {
    if (!image) {
      const payload = { prompt, history: history.map((m: any) => ({ role: m.role, content: m.content })) };
      return this.http.post<any>(this.nova2Chat, payload).pipe(map((r) => ({ text: r.text })));
    }
    return new Observable<{ text: string; image?: string }>((observer) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (!reader.result) { observer.error('Could not read file as base64'); return; }
        const base64Image = (reader.result as string).split(',')[1];
        const payload = {
          prompt,
          history: history.map((m: any) => ({ role: m.role, content: m.content })),
          image: base64Image
        };
        this.http.post<any>(this.nova2Chat, payload).pipe(
          catchError((err) => { observer.error(err); return throwError(() => err); })
        ).subscribe({
          next: (r) => { observer.next({ text: r.text, image: r.image }); observer.complete(); },
          error: (err) => observer.error(err)
        });
      };
      reader.onerror = (err) => observer.error(err);
      reader.readAsDataURL(image);
    });
  }
  
  generateNovaProText(
    prompt: string,
    history: Message[],
    image?: File
  ): Observable<{ text: string; image?: string }> {
    if (!image) {
      const payload = { prompt, history: history.map((m: any) => ({ role: m.role, content: m.content })) };
      return this.http.post<any>(this.novaProChat, payload).pipe(map((r) => ({ text: r.text })));
    }
    return new Observable<{ text: string; image?: string }>((observer) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (!reader.result) { observer.error('Could not read file as base64'); return; }
        const base64Image = (reader.result as string).split(',')[1];
        const payload = {
          prompt,
          history: history.map((m: any) => ({ role: m.role, content: m.content })),
          image: base64Image
        };
        this.http.post<any>(this.novaProChat, payload).pipe(
          catchError((err) => { observer.error(err); return throwError(() => err); })
        ).subscribe({
          next: (r) => { observer.next({ text: r.text, image: r.image }); observer.complete(); },
          error: (err) => observer.error(err)
        });
      };
      reader.onerror = (err) => observer.error(err);
      reader.readAsDataURL(image);
    });
  }
  
  generateScoutText(prompt: string, history: Message[], length?: AnswerLength): Observable<string> {
    const payload = { prompt, history, length };
    return this.http.post<any>(this.scoutLambda1, payload).pipe(map((resp) => resp.text));
  }
  

  
  generateCohereText(prompt: string, history: Message[]): Observable<string> {
    const payload = { prompt, history: history.map((msg: any) => ({ role: msg.role, content: msg.content })) };
    return this.http.post<any>(this.CohereChat, payload).pipe(map((resp) => resp.model_output));
  }

  generateCommandLightText(prompt: string, history: Message[]): Observable<string> {
    const payload = { prompt, history: history.map((msg: any) => ({ role: msg.role, content: msg.content })) };
    return this.http.post<any>(this.commandLight, payload).pipe(
      map((raw) => this.extractText(this.unwrapLambdaFunctionUrlJsonBody(raw)).trim())
    );
  }
  
  // =========================================================
  // ============== Multimodal / Images ======================
  // =========================================================
  generateClaude4TextImage(
    prompt: string,
    history: Message[],
    image?: File
  ): Observable<{ text: string; image?: string }> {
    if (!image) {
      const payload = { prompt, history: history.map((m: any) => ({ role: m.role, content: m.content })) };
      return this.http
        .post<any>(this.claude4ChatImage, payload, { headers: { 'Content-Type': 'application/json' } })
        .pipe(map((r) => ({ text: r.text as string })));
    }
    
    return new Observable<{ text: string; image?: string }>((observer) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string | null;
        if (!result) { observer.error('Could not read file as base64'); return; }
        const base64Image = result.split(',')[1];
        const payload = {
          prompt,
          history: history.map((m: any) => ({ role: m.role, content: m.content })),
          image: base64Image
        };
        this.http.post<any>(this.claude4ChatImage, payload, { headers: { 'Content-Type': 'application/json' } })
          .pipe(
            map((r) => ({ text: r.text as string, image: r.image as string | undefined })),
            catchError((err) => { observer.error(err); return throwError(() => err); })
          )
          .subscribe({
            next: (out) => { observer.next(out); observer.complete(); },
            error: (err) => observer.error(err)
          });
      };
      reader.onerror = (err) => observer.error(err);
      reader.readAsDataURL(image);
    });
  }
  
  generateClaude4Text(prompt: string, history: Message[]): Observable<string> {
    return this.generateClaude4TextImage(prompt, history).pipe(map((out) => out?.text ?? ''));
  }
  
  generatePixtralImageText(
    prompt: string,
    history: Message[],
    image?: File
  ): Observable<{ text: string; image?: string }> {
    if (!image) {
      const payload = { prompt, history: history.map((m: any) => ({ role: m.role, content: m.content })) };
      return this.http.post<any>(this.pistralImageChat, payload).pipe(map((r) => ({ text: r.text })));
    }
    return new Observable<{ text: string; image?: string }>((observer) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (!reader.result) { observer.error('Could not read file as base64'); return; }
        const base64Image = (reader.result as string).split(',')[1];
        const dataUrl = `data:${image.type || 'image/png'};base64,${base64Image}`;
        const payload: any = {
          prompt,
          history: history.map((m: any) => ({ role: m.role, content: m.content })),
          image: base64Image,
          image_url: dataUrl,
          reference_image: {
            type: 'image_url',
            image_url: dataUrl
          }
        };
        this.http.post<any>(this.pistralImageChat, payload).pipe(
          catchError((err) => { observer.error(err); return throwError(() => err); })
        ).subscribe({
          next: (r) => { observer.next({ text: r.text, image: r.image }); observer.complete(); },
          error: (err) => observer.error(err)
        });
      };
      reader.onerror = (err) => observer.error(err);
      reader.readAsDataURL(image);
    });
  }

  private readImagePayload(file: File): Observable<{ base64: string; mimeType: string; dataUrl: string }> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        if (!result) {
          observer.error(new Error('Could not read image file'));
          return;
        }
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        observer.next({
          base64,
          mimeType: file.type || 'image/png',
          dataUrl: result
        });
        observer.complete();
      };
      reader.onerror = () => observer.error(new Error('Could not read image file'));
      reader.readAsDataURL(file);
    });
  }
  
  generateOldDalEImage(
    prompt: string,
    {
      size = '1024x1024',
      model = 'dall-e-3',
      responseFormat = 'url',
      n = 1
    }: { size?: string; model?: string; responseFormat?: 'url' | 'b64_json'; n?: number } = {}
  ): Observable<string> {
    const payload = { prompt, model, n, size, response_format: responseFormat };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.openAIKey}`
    });
    return this.http
      .post<any>('https://api.openai.com/v1/images/generations', payload, { headers })
      .pipe(
        map((resp) => {
          const data = resp?.data?.[0];
          return responseFormat === 'b64_json' ? data?.b64_json : data?.url;
        })
      );
  }
  
  generateDalE3Image(
    prompt: string,
    {
      size = '1024x1024',
      quality = 'hd',
      responseFormat = 'url',
      n = 1
    }: {
      size?: '1024x1024' | '1024x1536' | '1536x1024' | string;
      quality?: 'standard' | 'hd';
      responseFormat?: 'url' | 'b64_json';
      n?: number;
    } = {}
  ): Observable<{ image: string; images?: string[]; revisedPrompt: string; imageUrl?: string; imageId?: string }> {
    const payload = {
      prompt,
      model: 'dall-e-3',
      n,
      size,
      quality,
      response_format: responseFormat
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.openAIKey}`
    });
    
    return this.http.post<any>('https://api.openai.com/v1/images/generations', payload, { headers }).pipe(
      map((resp) => {
        const data = Array.isArray(resp?.data) ? resp.data : [];
        const images = data
          .map((item: any) => (responseFormat === 'b64_json' ? item?.b64_json : item?.url))
          .filter((img: string) => img && img.length > 0);
        const first = data[0] ?? {};
        return {
          image: images[0] || '',
          images: images.length > 1 ? images : undefined,
          revisedPrompt: first.revised_prompt ?? '',
          imageUrl: first.url ?? undefined,
          imageId: first.id ?? undefined
        };
      })
    );
  }
  
  generateDalE2LegacyImage(
    prompt: string,
    image: File,
    {
      size = '1024x1024',
      responseFormat = 'url',
      n = 1
    }: {
      size?: string;
      responseFormat?: 'url' | 'b64_json';
      n?: number;
    } = {}
  ): Observable<{ image: string; images?: string[]; imageUrl?: string; imageId?: string }> {
    const hasPrompt = Boolean(prompt?.trim());
    const endpoint = hasPrompt
      ? 'https://api.openai.com/v1/images/edits'
      : 'https://api.openai.com/v1/images/variations';
    
    const form = new FormData();
    form.append('model', 'dall-e-2');
    form.append('image', image, image.name);
    if (hasPrompt) form.append('prompt', prompt.trim());
    form.append('n', String(n));
    form.append('size', size);
    form.append('response_format', responseFormat);
    
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.openAIKey}`
    });
    
    return this.http.post<any>(endpoint, form, { headers }).pipe(
      map((resp) => {
        const data = Array.isArray(resp?.data) ? resp.data : [];
        const images = data
          .map((item: any) => (responseFormat === 'b64_json' ? item?.b64_json : item?.url))
          .filter((img: string) => img && img.length > 0);
        const first = data[0] ?? {};
        return {
          image: images[0] || '',
          images: images.length > 1 ? images : undefined,
          imageUrl: first.url ?? undefined,
          imageId: first.id ?? undefined
        };
      })
    );
  }
  
  generateDalEImage(
    prompt: string,
    {
      size = '1024x1024',
      quality = 'medium',
      background = 'auto',
      previousResponseId,
      n = 1
    }: {
      size?: '1024x1024' | '1024x1536' | '1536x1024' | string;
      quality?: 'low' | 'medium' | 'high' | 'standard';
      background?: 'auto' | 'transparent' | 'opaque';
      previousResponseId?: string;
      n?: number;
    } = {}
  ): Observable<{ image: string; images?: string[]; responseId: string; revisedPrompt: string }> {
    const tool: any = { type: 'image_generation' };
    if (size) tool.size = size;
    if (quality) tool.quality = quality;
    if (background) tool.background = background;
    
    const payload: any = {
      model: 'gpt-5',
      input: prompt,
      tools: [tool]
    };
    
    if (previousResponseId) {
      payload.previous_response_id = previousResponseId;
    }
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.openAIKey}`
    });
    
    return this.http.post<any>(this.openAIResponsesUrl, payload, { headers }).pipe(
      map((resp) => {
        const imgBlocks = (resp.output ?? []).filter(
          (o: any) => o.type === 'image_generation_call' && o.status === 'completed'
        );
        
        if (!imgBlocks.length || !imgBlocks[0]?.result) {
          throw new Error('Image generation failed: no result returned');
        }
        
        const images: string[] = imgBlocks
          .map((block: any) => block.result)
          .filter((img: string) => img && img.length > 0);
        
        return {
          image: images[0],
          images: images.length > 1 ? images : undefined,
          responseId: resp.id as string,
          revisedPrompt: imgBlocks[0].revised_prompt ?? ''
        };
      })
    );
  }
  
  generateGeminiProImage(
    prompt: string,
    history: Message[] = [],
    image?: File,
    options: { model?: string; aspectRatio?: string; imageSize?: string } = {}
  ): Observable<{ image: string; text: string; imageRef?: ImageRef | null; model?: string }> {
    const cleanPrompt = String(prompt || '').trim();
    if (!cleanPrompt) {
      return throwError(() => new Error('Missing prompt'));
    }
    
    if (!this.geminiProImageUrl) {
      return throwError(() => new Error('Missing Gemini Pro Image endpoint configuration.'));
    }
    
    const model = (options.model || 'gemini-3-pro-image-preview').trim();
    const aspectRatio = (options.aspectRatio || '1:1').trim();
    const imageSize = (options.imageSize || '1K').trim();
    
    const safeHistory = history || [];
    const baseHistory = safeHistory.map((m: any) => ({
      role: m?.role || 'user',
      content: m?.content || ''
    }));
    
    const lastImageRef = [...safeHistory]
      .reverse()
      .map((m: any) => m?.imageRef)
      .find((ref: any) => ref && typeof ref?.fileUri === 'string');
    
    const makeRequest = (referenceImagePart?: { inline_data: { mime_type: string; data: string } }) => {
      const payload: any = {
        prompt: cleanPrompt,
        history: referenceImagePart
          ? [...baseHistory, { role: 'user', parts: [referenceImagePart] }]
          : baseHistory,
        model,
        aspectRatio,
        imageSize
      };
      
      if (lastImageRef) {
        payload.imageRef = lastImageRef;
      }
      
      return this.http.post<any>(this.geminiProImageUrl, payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      });
    };
    
    const req$ = image
      ? this.fileToDataUrl(image).pipe(
          switchMap((dataUrl) => {
            const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
            return makeRequest({
              inline_data: {
                mime_type: image.type || 'image/png',
                data: b64
              }
            });
          })
        )
      : makeRequest();
    
    return req$.pipe(
      map((resp: any) => ({
        image: String(resp?.imageBase64 || ''),
        text: String(resp?.text || ''),
        imageRef: resp?.imageRef || null,
        model: String(resp?.model || model)
      })),
      catchError((err: any) => {
        const message = err?.error?.message || err?.error?.error || err?.message || 'Gemini Pro image request failed.';
        return throwError(() => new Error(String(message)));
      })
    );
  }
  
  editDalEImage(
    previousResponseId: string,
    editInstruction: string,
    options?: {
      size?: string;
      quality?: 'low' | 'medium' | 'high' | 'standard';
      background?: 'auto' | 'transparent' | 'opaque';
    }
  ): Observable<{ image: string; responseId: string; revisedPrompt: string }> {
    return this.generateDalEImage(editInstruction, {
      ...options,
      previousResponseId
    });
  }
  
  generateStabilityImage(params: { prompt: string; numberOfImages?: number }): Observable<{ image: string; images: string[] }> {
    const p = (params.prompt ?? '').trim();
    if (!p) {
      return throwError(() => new Error('Missing required prompt'));
    }
    
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const payload = {
      prompt: p,
      mode: 'text-to-image',
      aspect_ratio: '1:1',
      output_format: 'png'
    };
    
    return this.http
      .post<unknown>(this.stabilityAPI, payload, { headers })
      .pipe(
        map((raw) => {
          const resp = this.unwrapLambdaFunctionUrlJsonBody(raw) as StabilityLambdaResponse;
          const format = String(resp?.output_format || 'png').toLowerCase();
          
          let b64 = '';
          
          if (Array.isArray(resp?.images) && resp.images.length > 0) {
            b64 = resp.images[0];
          } else if (resp?.image) {
            b64 = resp.image;
          }
          
          if (!b64) {
            throw new Error('No image returned from Stability Lambda');
          }
          
          const dataUrl = `data:image/${format};base64,${b64}`;
          
          return { image: dataUrl, images: [dataUrl] };
        }),
        catchError((err: any) => {
          const status = err?.status;
          const body = this.unwrapLambdaFunctionUrlJsonBody(err?.error);
          const bodyStr = typeof body === 'string' ? body : (body ? JSON.stringify(body) : '');
          if (status === 400 && (bodyStr?.toLowerCase().includes('filter') || body?.reason === 'Filter reason: prompt')) {
            return throwError(() => new Error('This prompt was blocked by the content policy. Please try a different description.'));
          }
          if (err?.error?.error) {
            return throwError(() => new Error(String(err.error.error)));
          }
          return throwError(() => err);
        })
      );
  }

  /**
   * Stability SD 3.5 Large on Bedrock (Lambda). Request body matches server `_build_sd35_payload`:
   * text-to-image: prompt, mode, aspect_ratio, output_format, optional seed / negative_prompt.
   * image-to-image: prompt, mode, image (base64), strength, output_format.
   */
  generateStabilitySd35Large(params: {
    prompt: string;
    referenceImage?: File | null;
    aspectRatio?: string;
    outputFormat?: 'png' | 'jpeg' | 'webp';
    strength?: number;
  }): Observable<{ image: string; images: string[] }> {
    const prompt = (params.prompt ?? '').trim();
    if (!prompt) {
      return throwError(() => new Error('Missing required prompt'));
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const url = this.stabilitySd35LargeUrl;

    const buildBody = async (): Promise<Record<string, unknown>> => {
      const output_format = params.outputFormat || 'png';
      if (params.referenceImage) {
        const dataUrl = await firstValueFrom(this.fileToDataUrl(params.referenceImage));
        const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
        return {
          prompt,
          mode: 'image-to-image',
          image: b64,
          strength: typeof params.strength === 'number' ? params.strength : 0.75,
          output_format
        };
      }
      return {
        prompt,
        mode: 'text-to-image',
        aspect_ratio: params.aspectRatio || '1:1',
        output_format
      };
    };

    return from(buildBody()).pipe(
      switchMap((body) =>
        this.http.post<unknown>(url, body, { headers }).pipe(
          map((raw) => this.parseStabilitySd35LambdaResponse(raw)),
          catchError((err: any) => {
            const msg =
              err?.error?.error ||
              err?.error?.message ||
              (typeof err?.error === 'string' ? err.error : '') ||
              err?.message ||
              'Stability SD3.5 request failed.';
            return throwError(() => new Error(String(msg)));
          })
        )
      )
    );
  }

  private parseStabilitySd35LambdaResponse(raw: unknown): { image: string; images: string[] } {
    let bedrock: any = raw;
    if (raw && typeof raw === 'object' && raw !== null && 'body' in (raw as object)) {
      const b = (raw as { body?: unknown }).body;
      if (typeof b === 'string') {
        try {
          bedrock = JSON.parse(b);
        } catch {
          throw new Error('Invalid Stability SD3.5 response body');
        }
      } else {
        bedrock = b;
      }
    }
    if (bedrock?.error) {
      throw new Error(String(bedrock.error));
    }
    const imagesArr: string[] = Array.isArray(bedrock?.images) ? bedrock.images : [];
    const b64 = (imagesArr[0] || bedrock?.image || '') as string;
    if (!b64 || typeof b64 !== 'string' || !b64.trim()) {
      throw new Error('No image returned from Stability SD3.5');
    }
    const fmt = String(bedrock?.output_format || 'png').toLowerCase();
    const dataUrl = b64.startsWith('data:') ? b64 : `data:image/${fmt};base64,${b64}`;
    return { image: dataUrl, images: [dataUrl] };
  }

  // =========================================================
  // ================== DeepSeek / o3 ========================
  // =========================================================
  generateDeepSeekText(prompt: string, history: Message[]): Observable<string> {
    const payload = { prompt, history: history.map((msg: any) => ({ role: msg.role, content: msg.content })) };
    return this.http.post<any>(this.deepseekChat, payload).pipe(map((resp) => String(resp.text ?? '').trim()));
  }
  
  /** DeepSeek V3.2 — Lambda Function URL (us-west-2). */
  generateDeepSeekV3Text(prompt: string, history: Message[]): Observable<string> {
    const payload = {
      prompt,
      history: history.map((msg) => ({
        role: msg.role,
        content: msg.content
      }))
    };

    return this.http.post<{ text: string }>(this.deepSeekV32Chat, payload).pipe(
      map((resp) => resp.text?.trim() ?? '')
    );
  }
  
  generateDeepReasoningText(
    prompt: string,
    history: Message[] = [],
    effort: 'low' | 'medium' | 'deep' = 'deep'
  ): Observable<{ text: string; tokens: number }> {
    const input = [...history.map((m: any) => ({ role: m.role, content: m.content })), { role: 'user', content: prompt }];
    const payload = { model: 'o3', reasoning: { effort }, input };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.openAIKey}`
    });
    return this.http.post<any>(this.openAIResponsesUrl, payload, { headers }).pipe(
      map((r) => {
        const msgBlock = r.output?.find((b: any) => b.type === 'message');
        const text = msgBlock?.content?.[0]?.text ?? '';
        const tokens = r.usage?.total_tokens ?? 0;
        return { text, tokens };
      })
    );
  }
  
  // =========================================================
  // ================== Audio / Realtime =====================
  // =========================================================
  private cleanupRealtimeSession(): void {
    const current = this.activeRealtime;
    this.activeRealtime = null;
    
    try { current?.close(); } catch {}
    
    this.sessionActive$.next(false);
    this.remoteAudioEl$.next(null);
    this.partialUserTranscript$.next('');
    this.resetRealtimeAnalyzers();
  }
  
  private deriveRealtimeProxyUrls(): { sessionUrl: string; wsUrl: string } {
    const raw = this.openAIRealtimeProxyUrl?.trim();
    if (!raw) {
      throw new Error('Missing openAIRealtimeProxyUrl in environment.');
    }
    
    const url = new URL(raw);
    const isWs = url.protocol === 'ws:' || url.protocol === 'wss:';
    const wsProtocol = isWs ? url.protocol : (url.protocol === 'https:' ? 'wss:' : 'ws:');
    const httpProtocol = isWs ? (url.protocol === 'wss:' ? 'https:' : 'http:') : url.protocol;
    const path = url.pathname.replace(/\/+$/, '');
    
    const wsUrl = isWs
      ? `${url.protocol}//${url.host}${path}`
      : `${wsProtocol}//${url.host}${path.endsWith('/ws') ? path : path}`;
    
    const sessionUrl = this.openAIRealtimeSessionUrl
      ? this.openAIRealtimeSessionUrl
      : `${httpProtocol}//${url.host}${path.endsWith('/session') ? path : `${path}/session`}`;
    
    return { sessionUrl, wsUrl };
  }
  
  private async getIdTokenSafe(): Promise<string | null> {
    try {
      return await getAuth().currentUser?.getIdToken?.();
    } catch {
      return null;
    }
  }
  
  private buildRealtimeSessionPayload(opts: { voice?: string; instructions?: string }) {
    return {
      model: this.REALTIME_MODEL,
      voice: opts.voice || this.DEFAULT_VOICE,
      instructions:
        opts.instructions ||
        'You are a helpful AI assistant. Keep responses conversational.',
      transcription_model: this.REALTIME_TRANSCRIBE_MODEL,
      locale: this.DEFAULT_LOCALE,
      timezone: this.DEFAULT_TIMEZONE
    };
  }
  
  private async createRealtimeProxySession(opts: {
    voice?: string;
    instructions?: string;
  }): Promise<ServerRtcSessionBootstrap> {
    const { sessionUrl, wsUrl } = this.deriveRealtimeProxyUrls();
    const payload = this.buildRealtimeSessionPayload(opts);
    const idToken = await this.getIdTokenSafe();
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
    });
    
    try {
      const resp = await firstValueFrom(
        this.http.post<any>(sessionUrl, payload, { headers })
      );
      
      const sessionId =
        String(resp?.sessionId || resp?.id || resp?.session_id || crypto.randomUUID()).trim();
      const returnedWsUrl =
        String(resp?.wsUrl || resp?.ws_url || '').trim() ||
        `${wsUrl}${wsUrl.includes('?') ? '&' : '?'}sessionId=${encodeURIComponent(sessionId)}`;
      
      return {
        sessionId,
        wsUrl: returnedWsUrl
      };
    } catch (error) {
      const fallbackSessionId = crypto.randomUUID();
      return {
        sessionId: fallbackSessionId,
        wsUrl: `${wsUrl}${wsUrl.includes('?') ? '&' : '?'}sessionId=${encodeURIComponent(fallbackSessionId)}`
      };
    }
  }
  
  private async waitForWebSocketOpen(ws: WebSocket, timeoutMs = this.realtimeWsOpenTimeoutMs): Promise<void> {
    if (ws.readyState === WebSocket.OPEN) return;
    
    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('Realtime WebSocket connection timed out.'));
      }, timeoutMs);
      
      const onOpen = () => {
        cleanup();
        resolve();
      };
      
      const onError = () => {
        cleanup();
        reject(new Error('Realtime WebSocket failed to open.'));
      };
      
      const cleanup = () => {
        clearTimeout(timeout);
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
      };
      
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
    });
  }
  
  private safeParseRealtimeMessage(raw: any): any {
    if (raw == null) return null;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return { type: 'message', text: raw };
      }
    }
    if (raw instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder().decode(raw);
        return JSON.parse(text);
      } catch {
        return null;
      }
    }
    return raw;
  }
  
  private normalizeRealtimeServerEvent(msg: any): any | null {
    if (!msg || typeof msg !== 'object') return null;
    
    if (msg.type === 'oai.server.event' && msg.event) {
      return msg.event;
    }
    
    if (
      typeof msg.type === 'string' &&
      (
        msg.type.startsWith('response.') ||
        msg.type.startsWith('conversation.') ||
        msg.type.startsWith('input_audio_buffer.') ||
        msg.type.startsWith('session.') ||
        msg.type === 'error'
      )
    ) {
      return msg;
    }
    
    if (msg.event && typeof msg.event?.type === 'string') {
      return msg.event;
    }
    
    return null;
  }
  
  private sendWsJson(ws: WebSocket | null, payload: RealtimeTransportMessage | any): void {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  }
  
  private getRealtimeSessionUpdateEvent(opts: { voice?: string; instructions?: string }): any {
    return {
      type: 'session.update',
      session: {
        model: this.REALTIME_MODEL,
        voice: opts.voice || this.DEFAULT_VOICE,
        instructions:
          opts.instructions ||
          'You are a helpful AI assistant. Keep responses conversational.',
        input_audio_transcription: {
          model: this.REALTIME_TRANSCRIBE_MODEL,
          language: this.DEFAULT_LOCALE.split('-')[0]
        },
        turn_detection: {
          type: 'server_vad'
        }
      }
    };
  }
  
  private startHeartbeat(wsGetter: () => WebSocket | null, sessionId: string): number {
    return window.setInterval(() => {
      const ws = wsGetter();
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      this.sendWsJson(ws, {
        type: 'ping',
        ts: Date.now(),
        sessionId,
      } satisfies RealtimeTransportMessage);
    }, this.realtimeHeartbeatMs);
  }
  
  private async startOpenAiRtcSession(opts: {
    voice?: string;
    instructions?: string;
  }): Promise<InternalOpenAiRtcSession> {
    const bootstrap = await this.createRealtimeProxySession(opts);
    const eventsSubject = new Subject<any>();
    const remoteAudioEl = new Audio();
remoteAudioEl.autoplay = true;
// Remove the playsInline line - it's only for video elements
    
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });
    
    const dc = pc.createDataChannel('oai-events', { ordered: true });
    
    let currentWs: WebSocket | null = null;
    let localStream: MediaStream | null = null;
    let heartbeatId: number | null = null;
    let reconnectAttempts = 0;
    let manuallyClosed = false;
    
    const clearHeartbeat = () => {
      if (heartbeatId != null) {
        clearInterval(heartbeatId);
        heartbeatId = null;
      }
    };
    
    const closeResources = () => {
      clearHeartbeat();
      
      try { currentWs?.close(); } catch {}
      currentWs = null;
      
      try { dc.close(); } catch {}
      try { pc.getSenders().forEach((sender) => pc.removeTrack(sender)); } catch {}
      try { pc.close(); } catch {}
      
      localStream?.getTracks().forEach((track) => {
        try { track.stop(); } catch {}
      });
      localStream = null;
      
      try {
        remoteAudioEl.pause();
        remoteAudioEl.srcObject = null;
      } catch {}
      
      this.resetRealtimeAnalyzers();
      eventsSubject.complete();
    };
    
    const forwardServerEvent = (message: any) => {
      const normalized = this.normalizeRealtimeServerEvent(message);
      if (normalized) {
        eventsSubject.next(normalized);
        return;
      }
      
      if (message?.type === 'error') {
        eventsSubject.next({ type: 'error', error: message?.error || message });
      }
    };
    
    const sendOpenAiClientEvent = (event: any) => {
      this.sendWsJson(currentWs, {
        type: 'oai.client.event',
        sessionId: bootstrap.sessionId,
        event
      } satisfies RealtimeTransportMessage);
    };
    
    const connectWebSocket = async (resume = false): Promise<void> => {
      const ws = new WebSocket(bootstrap.wsUrl);
      currentWs = ws;
      await this.waitForWebSocketOpen(ws);
      
      ws.addEventListener('message', async (evt) => {
        const msg = this.safeParseRealtimeMessage(evt.data);
        if (!msg) return;
        
        if (msg.type === 'rtc.answer' && msg.sdp) {
          if (!pc.currentRemoteDescription) {
            await pc.setRemoteDescription({
              type: 'answer',
              sdp: msg.sdp
            });
          }
          return;
        }
        
        if (msg.type === 'rtc.ice' && msg.candidate) {
          try {
            await pc.addIceCandidate(msg.candidate);
          } catch {
            // ignore bad remote candidate
          }
          return;
        }
        
        if (msg.type === 'pong') return;
        
        forwardServerEvent(msg);
      });
      
      ws.addEventListener('close', () => {
        clearHeartbeat();
        
        if (manuallyClosed) return;
        
        if (reconnectAttempts >= this.realtimeMaxReconnectAttempts) {
          eventsSubject.next({
            type: 'error',
            error: new Error('Realtime socket disconnected and reconnect attempts were exhausted.')
          });
          closeResources();
          this.sessionActive$.next(false);
          this.remoteAudioEl$.next(null);
          this.activeRealtime = null;
          return;
        }
        
        reconnectAttempts += 1;
        const delayMs = this.realtimeReconnectBaseDelayMs * reconnectAttempts;
        
        eventsSubject.next({
          type: 'transport.reconnecting',
          attempt: reconnectAttempts,
          delayMs
        });
        
        window.setTimeout(() => {
          if (manuallyClosed) return;
          void connectWebSocket(true).catch((err) => {
            eventsSubject.next({ type: 'error', error: err });
          });
        }, delayMs);
      });
      
      ws.addEventListener('error', () => {
        eventsSubject.next({
          type: 'transport.error',
          error: new Error('Realtime transport error.')
        });
      });
      
      heartbeatId = this.startHeartbeat(() => currentWs, bootstrap.sessionId);
      
      if (resume) {
        this.sendWsJson(ws, {
          type: 'session.resume',
          sessionId: bootstrap.sessionId
        } satisfies RealtimeTransportMessage);
        
        sendOpenAiClientEvent(this.getRealtimeSessionUpdateEvent(opts));
      } else {
        sendOpenAiClientEvent(this.getRealtimeSessionUpdateEvent(opts));
      }
    };
    
    try {
      localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
    } catch {
      throw new Error('Microphone access denied.');
    }
    
    localStream.getTracks().forEach((track) => pc.addTrack(track, localStream!));
    this.bindRealtimeAnalyzers(localStream, remoteAudioEl);
    
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendWsJson(currentWs, {
          type: 'rtc.ice',
          sessionId: bootstrap.sessionId,
          candidate: event.candidate.toJSON()
        } satisfies RealtimeTransportMessage);
      }
    };
    
    pc.ontrack = async (event) => {
      const [stream] = event.streams;
      if (event.track.kind === 'audio' && stream) {
        remoteAudioEl.srcObject = stream;
        try {
          await remoteAudioEl.play();
        } catch {
          // autoplay can still be blocked depending on browser gesture rules
        }
        this.bindRealtimeAnalyzers(localStream, remoteAudioEl);
      }
    };
    
    dc.onmessage = (event) => {
      const msg = this.safeParseRealtimeMessage(event.data);
      if (msg) {
        forwardServerEvent(msg);
      }
    };
    
    dc.onopen = () => {
      eventsSubject.next({ type: 'transport.data_channel.open' });
    };
    
    dc.onerror = () => {
      eventsSubject.next({
        type: 'transport.data_channel.error',
        error: new Error('RTC data channel error.')
      });
    };
    
    pc.onconnectionstatechange = () => {
      eventsSubject.next({
        type: 'transport.peer_connection.state',
        state: pc.connectionState
      });
      
      if (pc.connectionState === 'failed') {
        eventsSubject.next({
          type: 'error',
          error: new Error('Peer connection failed.')
        });
      }
    };
    
    await connectWebSocket(false);
    
    const offer = await pc.createOffer({
      offerToReceiveAudio: true
    });
    await pc.setLocalDescription(offer);
    
    this.sendWsJson(currentWs, {
      type: 'rtc.offer',
      sessionId: bootstrap.sessionId,
      sdp: offer.sdp || ''
    } satisfies RealtimeTransportMessage);
    
    const session: InternalOpenAiRtcSession = {
      sessionId: bootstrap.sessionId,
      events$: eventsSubject.asObservable(),
      pc,
      dc,
      localStream,
      remoteAudioEl,
      sendText: (text: string) => {
        const clean = String(text ?? '').trim();
        if (!clean) return;
        
        sendOpenAiClientEvent({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: clean }]
          }
        });
        
        sendOpenAiClientEvent({
          type: 'response.create'
        });
      },
      muteMic: (muted: boolean) => {
        localStream?.getAudioTracks().forEach((track) => {
          track.enabled = !muted;
        });
      },
      close: () => {
        if (manuallyClosed) return;
        manuallyClosed = true;
        
        this.sendWsJson(currentWs, {
          type: 'oai.client.event',
          sessionId: bootstrap.sessionId,
          event: { type: 'session.close' }
        } satisfies RealtimeTransportMessage);
        
        closeResources();
      }
    };
    
    return session;
  }
  
  audioWithGpt4o(args: {
    prompt?: string;
    history?: Message[];
    realtime: boolean;
    voice?: string;
    instructions?: string;
    onTextDelta?: (chunk: string) => void;
    onTextDone?: () => void;
    onError?: (e: any) => void;
  }): Observable<
    | string
    | {
        pc: RTCPeerConnection;
        dc: RTCDataChannel;
        localStream: MediaStream | null;
        remoteAudioEl: HTMLAudioElement;
        sendText: (text: string) => void;
        muteMic: (muted: boolean) => void;
        close: () => void;
      }
  > {
    return this.toObservable(async () => {
      if (!args.realtime) {
        const prompt = String(args.prompt ?? '').trim();
        const history = args.history ?? [];
        const text = await firstValueFrom(this.getChatGPT54Chat(prompt, history));
        return text || '';
      }
      
      this.cleanupRealtimeSession();
      
      const session = await this.startOpenAiRtcSession({
        voice: args.voice || this.DEFAULT_VOICE,
        instructions:
          args.instructions ||
          'You are a helpful AI assistant. Keep responses conversational.'
      });
      
      let currentUserTranscript = '';
      
      const sub = session.events$.subscribe({
        next: (msg: any) => {
          const type = String(msg?.type || '');
          
          switch (type) {
            case 'input_audio_buffer.speech_started':
              currentUserTranscript = '';
              this.partialUserTranscript$.next('');
              this.userTranscriptDelta$.next('');
              break;
              
            case 'conversation.item.input_audio_transcription.delta': {
              const deltaText = String(msg?.delta || msg?.transcript || '');
              if (!deltaText) break;
              currentUserTranscript += deltaText;
              this.userTranscriptDelta$.next(deltaText);
              this.partialUserTranscript$.next(currentUserTranscript);
              break;
            }
            
            case 'conversation.item.input_audio_transcription.completed':
            case 'response.audio_transcript.done': {
              const finalTranscript = String(msg?.transcript || msg?.text || currentUserTranscript || '');
              if (finalTranscript.trim()) {
                this.userTranscriptDone$.next(finalTranscript.trim());
                this.partialUserTranscript$.next('');
              }
              currentUserTranscript = '';
              break;
            }
            
            case 'response.text.delta': {
              const delta = String(msg?.delta || msg?.text || '');
              if (!delta) break;
              this.textDelta$.next(delta);
              try { args.onTextDelta?.(delta); } catch {}
              break;
            }
            
            case 'response.done':
            case 'response.completed':
              this.textDone$.next();
              try { args.onTextDone?.(); } catch {}
              break;
              
            case 'error':
              this.error$.next(msg?.error || msg);
              try { args.onError?.(msg?.error || msg); } catch {}
              break;
              
            default:
              break;
          }
        },
        error: (e) => {
          this.error$.next(e);
          try { args.onError?.(e); } catch {}
        }
      });
      
      const originalClose = session.close;
      session.close = () => {
        try { sub.unsubscribe(); } catch {}
        originalClose();
        this.sessionActive$.next(false);
        this.remoteAudioEl$.next(null);
        this.partialUserTranscript$.next('');
        this.activeRealtime = null;
      };
      
      this.activeRealtime = {
        pc: session.pc,
        dc: session.dc,
        localStream: session.localStream,
        remoteAudioEl: session.remoteAudioEl,
        sendText: session.sendText,
        muteMic: session.muteMic,
        close: session.close
      };
      
      this.sessionActive$.next(true);
      this.remoteAudioEl$.next(session.remoteAudioEl);
      
      return this.activeRealtime;
    });
  }
  
  sendRealtimeImage(_file: File, _instruction?: string): Observable<void> {
    return this.toObservable(async () => {
      throw new Error('Realtime image injection is disabled. Audio + transcription only.');
    });
  }

  startOpenAiRtcUi(opts?: { voice?: string; instructions?: string }): Observable<OpenAiRtcSession> {
    return this.uiService.startOpenAiRtcSession(opts);
  }
  
  private extractStructuredSummary(res: any): CombinedSummaryResponse {
    const candidate = this.unwrapSummaryPayload(res) ?? {};

    const summaryText = this.firstNonEmptyString(
      candidate?.summary,
      candidate?.analysis,
      candidate?.analysis_text,
      candidate?.analysisText,
      candidate?.final_summary,
      candidate?.finalSummary,
      candidate?.answer,
      candidate?.result,
      candidate?.output,
      candidate?.message
    );

    const relevanceText = this.firstNonEmptyString(
      candidate?.relevance,
      candidate?.relevance_note,
      candidate?.relevanceNote,
      candidate?.relevance_summary,
      candidate?.relevanceSummary
    );

    const furtherAnalysisText = this.firstNonEmptyString(
      candidate?.further_analysis,
      candidate?.furtherAnalysis,
      candidate?.further_analysis_text,
      candidate?.next_steps,
      candidate?.nextSteps,
      candidate?.recommendation,
      candidate?.recommendations
    );

    const analysisText = this.firstNonEmptyString(
      candidate?.analysis,
      candidate?.analysis_text,
      candidate?.analysisText,
      candidate?.final_summary,
      candidate?.finalSummary,
      furtherAnalysisText
    );

    return {
      summary: summaryText || this.extractCombinedSummaryText(candidate),
      relevance: relevanceText || undefined,
      analysis: analysisText || undefined,
      furtherAnalysis: furtherAnalysisText || undefined
    };
  }

  private firstNonEmptyString(...values: any[]): string {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return '';
  }

  private extractCombinedSummaryText(res: any): string {
    const candidate = this.unwrapSummaryPayload(res);

    const textCandidates = [
      candidate?.summary,
      candidate?.analysis,
      candidate?.analysis_text,
      candidate?.analysisText,
      candidate?.final_summary,
      candidate?.finalSummary,
      candidate?.answer,
      candidate?.result,
      candidate?.output,
      candidate?.message,
      candidate?.relevance,
      candidate?.relevance_note,
      candidate?.relevanceNote,
      candidate?.further_analysis,
      candidate?.furtherAnalysis,
      candidate?.next_steps,
      candidate?.nextSteps
    ];

    const formatted = textCandidates
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => String(value).trim())
      .filter(Boolean);

    if (formatted.length > 0) {
      return formatted.join('\n\n');
    }

    const structured = Object.entries(candidate ?? {})
      .filter(([key]) => !['summary', 'analysis', 'analysis_text', 'analysisText', 'final_summary', 'finalSummary', 'answer', 'result', 'output', 'message', 'body'].includes(key.toLowerCase()))
      .map(([, value]) => this.stringifySummaryPart(value))
      .filter((value) => value.trim().length > 0);

    return structured.join('\n\n');
  }

  private unwrapSummaryPayload(res: any): any {
    if (res == null) return null;

    if (typeof res === 'string') {
      return res.trim() ? { summary: res } : null;
    }

    if (typeof res === 'object') {
      if (typeof (res as any).body === 'string') {
        try {
          const parsed = JSON.parse((res as any).body);
          return this.unwrapSummaryPayload(parsed);
        } catch {
          return (res as any).body;
        }
      }

      if (Array.isArray(res)) {
        return { summary: res.join('\n\n') };
      }

      if ('summary' in res || 'analysis' in res || 'relevance' in res || 'further_analysis' in res || 'furtherAnalysis' in res) {
        return res;
      }

      const nested = Object.values(res).find((value) => value && typeof value === 'object');
      if (nested && typeof nested === 'object') {
        return this.unwrapSummaryPayload(nested);
      }
    }

    return res;
  }

  private stringifySummaryPart(value: any): string {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return value.map((item) => this.stringifySummaryPart(item)).filter(Boolean).join('\n');
    if (typeof value === 'object') {
      const entries = Object.entries(value)
        .map(([key, item]) => {
          const text = this.stringifySummaryPart(item);
          return text ? `${key}: ${text}` : '';
        })
        .filter(Boolean);
      return entries.join('\n');
    }
    return '';
  }

  // =========================================================
  // ================== Summarization ========================
  // =========================================================
  callCombinedSummary(
    prompt: string,
    data: Record<string, any>,
    modelId?: string
  ): Observable<CombinedSummaryResponse> {
    const isIqCombinedSummary = modelId == null || modelId === '';
    const url = isIqCombinedSummary ? this.iqSummarySonnet46LambdaUrl : this.summaryLambdaUrl;
    const body = isIqCombinedSummary
      ? { prompt, data, modelId: AIFireService.iqCombinedSummaryModelId }
      : { prompt, data };
    return this.http
      .post<any>(url, body, {
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res) => {
          const structured = this.extractStructuredSummary(res);
          return {
            summary: structured.summary,
            analysis: structured.analysis,
            relevance: structured.relevance,
            furtherAnalysis: structured.furtherAnalysis
          };
        }),
        catchError((error) => {
          console.error(
            isIqCombinedSummary
              ? 'Error in IQ combined summary (Sonnet 4.6) lambda call:'
              : 'Error in summary lambda call:',
            error
          );
          return throwError(() => new Error(`Summary Lambda Error: ${error.message || error}`));
        })
      );
  }
}